import { useState, useMemo, useEffect, useCallback, lazy, Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Coins,
  Lock,
  MessageCircle,
  Plus,
  Swords,
  Loader2,
  DollarSign,
  ShoppingCart,
  User,
  Bookmark,
  LayoutGrid,
  List,
  Star,
  SlidersHorizontal,
  ChevronUp,
  Clock,
  Mail,
  Crown,
  LogOut,
  Handshake,
  Ban,
  X,
  Package,
  History,

} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useClientSession } from "@/hooks/useClientSession";
import { resolveAvatarUrl } from "@/components/ClientCadastroTab";
import stoneBg from "@/assets/stone-bg.webp";
import logo from "@/assets/logo.webp";
import tibiaCoinImg from "@/assets/rubini_coins.gif";
import tabItensImg from "@/assets/tab-itens.jpg";
import tabPersonagensImg from "@/assets/tab-personagens.jpg";
import tabCoinsImg from "@/assets/tab-coins.jpg";
import AppFooter from "@/components/AppFooter";
import TibiaContainer from "@/components/TibiaContainer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
const ClientMensagensTab = lazy(() => import("@/components/ClientMensagensTab"));

import MarketplaceCard from "@/components/MarketplaceCard";
import MarketplaceCardSkeleton from "@/components/MarketplaceCardSkeleton";
import MarketplaceListItemSkeleton from "@/components/MarketplaceListItemSkeleton";
import FeaturedCarousel from "@/components/FeaturedCarousel";
import MarketplaceListItem from "@/components/MarketplaceListItem";
import BatchStackedCards from "@/components/BatchStackedCards";
import ItemDetailModal from "@/components/ItemDetailModal";
import ServerCombobox from "@/components/ServerCombobox";
import ItemSearchCombobox from "@/components/ItemSearchCombobox";
import TierCombobox from "@/components/TierCombobox";
import AdvertiserCombobox from "@/components/AdvertiserCombobox";
import PersonagemCard from "@/components/PersonagemCard";
import PersonagemFilters, {
  defaultPersonagemFilters,
  filterPersonagemAnuncios,
  type PersonagemFilterState,
} from "@/components/personagem/PersonagemFilters";

// Lazy-load dialogs that are only shown on user interaction
const GoldCalculator = lazy(() => import("@/components/GoldCalculator"));
const TrocaCoinsHistoricoTab = lazy(() => import("@/components/TrocaCoinsHistoricoTab"));
const CreateAnuncioDialog = lazy(() => import("@/components/CreateAnuncioDialog"));
const CreateAnuncioPersonagemDialog = lazy(() => import("@/components/CreateAnuncioPersonagemDialog"));
const LoginDialog = lazy(() => import("@/components/LoginDialog"));
const BugReportDialog = lazy(() => import("@/components/BugReportDialog"));
const IntermedioChatDialog = lazy(() => import("@/components/IntermedioChatDialog"));
const AdvertiserProfileDialog = lazy(() => import("@/components/AdvertiserProfileDialog"));
import { useAnuncios, type Anuncio } from "@/hooks/useAnuncios";
import { fetchPublicAnunciosPaginated } from "@/lib/public-anuncios";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { invokeWithSessionGuard } from "@/lib/session-guard";
import { checkIsOwner } from "@/lib/ownership";
import { getSessionFingerprint } from "@/lib/session-fingerprint";
import { useInteressesSummary } from "@/hooks/useInteressesSummary";

const getFavorites = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem("rpgcoin_favorites") || "[]");
  } catch {
    return [];
  }
};

const Index = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { session, logout } = useClientSession();
  const { data: config } = useConfiguracoes();
  const isMobile = useIsMobile();
  const whatsappNumero = config?.whatsapp_numero;
  const [showBloqueado, setShowBloqueado] = useState(false);

  // Check if logged-in user is blocked on page load
  useEffect(() => {
    if (!session) return;
    const checkBlocked = async () => {
      try {
        const { data } = await supabase.functions.invoke("client-profile", {
          body: { contato: session.contato, token: session.token },
        });
        if (data?.error === "bloqueado" || data?.profile?.bloqueado) {
          setShowBloqueado(true);
          logout();
        }
      } catch {
        /* ignore */
      }
    };
    checkBlocked();
  }, [session]);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [filterCompra, setFilterCompra] = useState(true);
  const [filterVenda, setFilterVenda] = useState(true);
  const [filterPropostas, setFilterPropostas] = useState(false);
  const [servidorFilter, setServidorFilter] = useState<string[]>([]);
  const [tierFilter, setTierFilter] = useState<number[]>([]);
  const [minReais, setMinReais] = useState("");
  const [maxReais, setMaxReais] = useState("");
  const [minCoins, setMinCoins] = useState("");
  const [maxCoins, setMaxCoins] = useState("");
  const [minKk, setMinKk] = useState("");
  const [maxKk, setMaxKk] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createPersonagemOpen, setCreatePersonagemOpen] = useState(false);
  const [chooseTypeOpen, setChooseTypeOpen] = useState(false);
  const [personagemFilters, setPersonagemFilters] = useState<PersonagemFilterState>(defaultPersonagemFilters);
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showMyList, setShowMyList] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [showDestaques, setShowDestaques] = useState(false);
  const [showMyAds, setShowMyAds] = useState(false);
  const [detailItem, setDetailItem] = useState<any>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showPriceFilters, setShowPriceFilters] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [topTab, setTopTab] = useState<"itens" | "personagens" | "coins">("itens");
  const [coinsTab, setCoinsTab] = useState<"calculadora" | "historico">("calculadora");

  const {
    data: personagemAnuncios = [],
    isLoading: personagemLoading,
    refetch: refetchPersonagens,
  } = useQuery({
    queryKey: ["personagem-anuncios"],
    queryFn: async () => {
      const result = await fetchPublicAnunciosPaginated({ tipo: "personagem" });
      return result.anuncios.filter((a) => a.personagem);
    },
    enabled: topTab === "personagens",
    staleTime: 30_000,
  });
  // filteredPersonagemAnuncios is computed after myAllAds (below) so it can
  // include the owner's own paused/pending personagem ads, mirroring item cards.
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [advertiserFilter, setAdvertiserFilter] = useState<string[]>([]);
  const [profileDialogId, setProfileDialogId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedLotes, setExpandedLotes] = useState<Set<string>>(new Set());
  const toggleLote = useCallback((loteId: string) => {
    setExpandedLotes((prev) => {
      const next = new Set(prev);
      if (next.has(loteId)) {
        next.delete(loteId);
      } else {
        next.add(loteId);
      }
      return next;
    });
  }, []);
  const PAGE_SIZE = 50;
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [coinBalance, setCoinBalance] = useState(0);
  const [activeIntermediacaoId, setActiveIntermediacaoId] = useState<string | null>(null);
  const [intermediacaoByAnuncio, setIntermediacaoByAnuncio] = useState<Record<string, string>>({});
  const [intermediacaoMinimized, setIntermediacaoMinimized] = useState(false);
  const [hasActiveIntermediacao, setHasActiveIntermediacao] = useState(false);
  const [activeIntermediacoes, setActiveIntermediacoes] = useState<any[]>([]);
  const [unreadIntermediacaoIds, setUnreadIntermediacaoIds] = useState<Set<string>>(new Set());
  const [publicIntermediatingAnuncios, setPublicIntermediatingAnuncios] = useState<Set<string>>(new Set());
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const [interesseBalloonDismissed, setInteresseBalloonDismissed] = useState(false);
  const [draftBalloonDismissed, setDraftBalloonDismissed] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftSignature, setDraftSignature] = useState<string>("");

  const { data: interessesSummary } = useInteressesSummary(session?.contato, session?.token, session?.perfil_id);
  const interessesCount = interessesSummary?.activeItemsCount ?? 0;
  const interessesMatches = interessesSummary?.matches ?? [];
  const interesseItemNames = useMemo(
    () => new Set((interessesSummary?.allItemNames ?? []).map((n) => n.toLowerCase())),
    [interessesSummary],
  );

  // Set de IDs de anúncios que casam com itens de interesse cadastrados
  const interesseAnuncioIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of interessesMatches) {
      for (const a of m.anuncios || []) ids.add(a.id);
    }
    return ids;
  }, [interessesMatches]);

  // Build a signature for current interesse matches (sorted anuncio IDs).
  const interesseMatchesSignature = useMemo(() => {
    return Array.from(interesseAnuncioIds).sort().join(",");
  }, [interesseAnuncioIds]);

  // Persistent dismissal: hide balloon only if the dismissed signature
  // already covers ALL current matches. A new match → show again.
  const INTERESSE_DISMISS_KEY = "rpgcoin_interesse_balloon_dismissed_ids";
  const DRAFT_DISMISS_KEY = "rpgcoin_draft_balloon_dismissed_sig";

  const isInteresseDismissedPersistently = useMemo(() => {
    if (!session) return false;
    try {
      const raw = localStorage.getItem(INTERESSE_DISMISS_KEY);
      if (!raw) return false;
      const dismissedIds = new Set<string>(JSON.parse(raw));
      const currentIds = interesseMatchesSignature ? interesseMatchesSignature.split(",") : [];
      if (currentIds.length === 0) return true;
      return currentIds.every((id) => dismissedIds.has(id));
    } catch { return false; }
  }, [session, interesseMatchesSignature]);

  const isDraftDismissedPersistently = useMemo(() => {
    if (!session || !draftSignature) return false;
    try {
      const raw = localStorage.getItem(DRAFT_DISMISS_KEY);
      return raw === draftSignature;
    } catch { return false; }
  }, [session, draftSignature]);

  const showInteresseBalloon =
    !!session && interessesMatches.length > 0 && !interesseBalloonDismissed && !isInteresseDismissedPersistently;
  const showDraftBalloon =
    !!session && hasDraft && !draftBalloonDismissed && !isDraftDismissedPersistently && !createOpen;

  const dismissInteresseBalloon = useCallback((permanent: boolean) => {
    setInteresseBalloonDismissed(true);
    if (permanent) {
      try {
        const ids = interesseMatchesSignature ? interesseMatchesSignature.split(",") : [];
        localStorage.setItem(INTERESSE_DISMISS_KEY, JSON.stringify(ids));
      } catch { /* ignore */ }
    }
  }, [interesseMatchesSignature]);

  const dismissDraftBalloon = useCallback((permanent: boolean) => {
    setDraftBalloonDismissed(true);
    if (permanent && draftSignature) {
      try { localStorage.setItem(DRAFT_DISMISS_KEY, draftSignature); } catch { /* ignore */ }
    }
  }, [draftSignature]);

  // Detect draft anuncio in localStorage (belongs to current session)
  useEffect(() => {
    const check = () => {
      try {
        const draftStr = localStorage.getItem("rpgcoin_anuncio_draft");
        if (!draftStr || !session) { setHasDraft(false); setDraftSignature(""); return; }
        const draft = JSON.parse(draftStr);
        if (draft.contato && draft.contato !== session.contato) { setHasDraft(false); setDraftSignature(""); return; }
        const has = (draft.cart?.length > 0) || (draft.pendingItems?.length > 0) || !!draft.selectedItem;
        setHasDraft(!!has);
        // Signature reflects draft content; any edit → new signature → balloon returns.
        setDraftSignature(has ? draftStr : "");
      } catch { setHasDraft(false); setDraftSignature(""); }
    };
    check();
    const onStorage = (e: StorageEvent) => { if (e.key === "rpgcoin_anuncio_draft") check(); };
    window.addEventListener("storage", onStorage);
    const interval = setInterval(check, 3000);
    return () => { window.removeEventListener("storage", onStorage); clearInterval(interval); };
  }, [session?.contato, createOpen]);

  // Reset session-level dismissal when content changes so balloon can reappear
  useEffect(() => { setInteresseBalloonDismissed(false); }, [interesseMatchesSignature]);
  useEffect(() => { setDraftBalloonDismissed(false); }, [draftSignature]);


  // Fetch user's owned anuncio IDs from backend (full contact resolution)
  const { data: myAnuncioIds, isLoading: myAnuncioIdsLoading } = useQuery({
    queryKey: ["my-anuncio-ids", session?.contato, session?.token],
    queryFn: async () => {
      if (!session) return new Set<string>();
      const { data, error } = await invokeWithSessionGuard(() =>
        supabase.functions.invoke("client-anuncios", {
          body: { contato: session.contato, token: session.token, perfil_id: session.perfil_id },
        })
      );
      if (error || !data?.success) return new Set<string>();
      return new Set<string>((data.anuncios || []).map((a: any) => a.id));
    },
    enabled: !!session,
    staleTime: 60_000,
  });

  // Lock body scroll when mobile filters are open
  useEffect(() => {
    if (isMobile && filtersOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isMobile, filtersOpen]);

  // Fetch public list of anuncio IDs with active intermediations (visible to all users)
  useEffect(() => {
    const fetchPublicIntermediacao = async () => {
      try {
        const { data } = await supabase.functions.invoke("anuncios-intermediacao");
        if (data?.anuncio_ids) {
          setPublicIntermediatingAnuncios(new Set(data.anuncio_ids));
        }
      } catch {
        /* ignore */
      }
    };
    fetchPublicIntermediacao();

    // Refresh when intermediacoes table changes
    const channel = supabase
      .channel("public-intermediacoes-status")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "intermediacoes",
        },
        () => {
          fetchPublicIntermediacao();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Fetch unread message count and coin balance
  useEffect(() => {
    if (!session) {
      setUnreadCount(0);
      setCoinBalance(0);
      return;
    }
    const fetchUnread = async () => {
      try {
        const { data } = await supabase.functions.invoke("platform-messages", {
          body: { action: "unread_count", contato: session.contato, token: session.token },
        });
        if (data?.count != null) setUnreadCount(data.count);
      } catch {
        /* ignore */
      }
    };
    const fetchCoins = async () => {
      try {
        const { data } = await supabase.functions.invoke("client-coins", {
          body: { contato: session.contato, token: session.token },
        });
        if (data?.saldo != null) setCoinBalance(data.saldo);
      } catch {
        /* ignore */
      }
    };
    fetchUnread();
    fetchCoins();

    // Check for active intermediations and auto-open chat
    const checkIntermediacao = async () => {
      try {
        const { data } = await invokeWithSessionGuard(() =>
          supabase.functions.invoke("intermediacao", {
            body: { action: "listar", contato: session.contato, token: session.token },
          })
        );
        if (!data) return;
        const activeStatuses = [
          "aguardando_anunciante",
          "aguardando_pagamento_intermedio",
          "aguardando_intermediador",
          "em_andamento",
          "aguardando_pagamento_item",
          "aguardando_conferencia_admin",
          "aguardando_envio_item",
          "aguardando_confirmacao_recebimento",
          "aguardando_pagamento_anunciante",
          "aguardando_confirmacao_pagamento",
        ];
        const intermediacoes = data?.intermediacoes || [];
        const activeList = intermediacoes.filter((i: any) => activeStatuses.includes(i.status));

        // Build proxy image URLs for each intermediation using item name
        const enrichedList = activeList.map((i: any) => {
          const itemNome = i.anuncios?.item_nome || "";
          const formatted = itemNome.replace(/ /g, "_");
          const proxyUrl = formatted
            ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`
            : null;
          return {
            ...i,
            _item_image_url: proxyUrl,
          };
        });

        // Build anuncio -> intermediacao map
        const map: Record<string, string> = {};
        for (const inter of activeList) {
          if (inter.anuncio_id) map[inter.anuncio_id] = inter.id;
        }
        setIntermediacaoByAnuncio(map);
        setHasActiveIntermediacao(activeList.length > 0);
        setActiveIntermediacoes(enrichedList);

        // Honor a requested intermediation id (e.g., from global notification on other routes)
        let requestedId: string | null = null;
        try {
          requestedId = sessionStorage.getItem("rpgcoin_open_intermediacao");
        } catch { /* ignore */ }
        const requested = requestedId ? activeList.find((i: any) => i.id === requestedId) : null;
        const active = requested || activeList[0];
        if (requested) {
          try { sessionStorage.removeItem("rpgcoin_open_intermediacao"); } catch { /* ignore */ }
          setActiveIntermediacaoId(requested.id);
          setIntermediacaoMinimized(false);
        } else if (active && !activeIntermediacaoId) {
          setActiveIntermediacaoId(active.id);
          setIntermediacaoMinimized(false);
        }
        // If no more active, clear state
        if (activeList.length === 0) {
          setHasActiveIntermediacao(false);
          setIntermediacaoMinimized(false);
          setActiveIntermediacoes([]);
        }
      } catch {
        /* ignore */
      }
    };
    checkIntermediacao();

    // Realtime subscription for intermediation updates
    const interChannel = supabase
      .channel("index-intermediacoes-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "intermediacoes",
        },
        () => {
          // Re-check intermediations when any change occurs
          checkIntermediacao();
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "intermediacoes_pagamentos",
        },
        () => {
          checkIntermediacao();
        },
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "intermediacoes_mensagens",
        },
        (payload: any) => {
          const interId = payload.new?.intermediacao_id;
          if (interId) {
            setUnreadIntermediacaoIds((prev) => {
              const next = new Set(prev);
              next.add(interId);
              return next;
            });
          }
        },
      )
      .subscribe();

    // Only poll when tab is visible to avoid background accumulation
    const interval = setInterval(() => {
      if (!document.hidden) fetchUnread();
    }, 30000);
    return () => {
      clearInterval(interval);
      supabase.removeChannel(interChannel);
    };
  }, [session]);

  const requireLogin = useCallback((action: () => void) => {
    if (session) {
      action();
    } else {
      setPendingAction(() => action);
      setLoginOpen(true);
    }
  }, [session]);

  const handleLoginSuccess = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const tipoFilter = filterCompra && filterVenda ? "" : filterCompra ? "compra" : filterVenda ? "venda" : "";

  // Debounce server-side filters to avoid excessive API calls
  const [debouncedServerFilters, setDebouncedServerFilters] = useState({
    servidores: [] as string[],
    tiers: [] as number[],
    minReais: undefined as number | undefined,
    maxReais: undefined as number | undefined,
    minCoins: undefined as number | undefined,
    maxCoins: undefined as number | undefined,
    minKk: undefined as number | undefined,
    maxKk: undefined as number | undefined,
    itemNames: [] as string[],
    apelidos: [] as string[],
    propostasOnly: false,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedServerFilters({
        servidores: servidorFilter,
        tiers: tierFilter,
        minReais: minReais ? parseFloat(minReais) : undefined,
        maxReais: maxReais ? parseFloat(maxReais) : undefined,
        minCoins: minCoins ? parseFloat(minCoins) : undefined,
        maxCoins: maxCoins ? parseFloat(maxCoins) : undefined,
        minKk: minKk ? parseFloat(minKk) : undefined,
        maxKk: maxKk ? parseFloat(maxKk) : undefined,
        itemNames: selectedItems,
        apelidos: advertiserFilter,
        propostasOnly: filterPropostas,
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [servidorFilter, tierFilter, minReais, maxReais, minCoins, maxCoins, minKk, maxKk, selectedItems, advertiserFilter, filterPropostas]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [tipoFilter, showExpired, debouncedServerFilters, showMyList, showDestaques, showMyAds, filterCompra, filterVenda]);

  const {
    data: anunciosResult,
    isLoading,
    isFetching,
    refetch: refetchAnuncios,
  } = useAnuncios({
    tipo: tipoFilter || undefined,
    excludeTipos: ["personagem"],
    includeExpired: showExpired,
    servidores: debouncedServerFilters.servidores.length > 0 ? debouncedServerFilters.servidores : undefined,
    tiers: debouncedServerFilters.tiers.length > 0 ? debouncedServerFilters.tiers : undefined,
    minReais: debouncedServerFilters.minReais,
    maxReais: debouncedServerFilters.maxReais,
    minCoins: debouncedServerFilters.minCoins,
    maxCoins: debouncedServerFilters.maxCoins,
    minKk: debouncedServerFilters.minKk,
    maxKk: debouncedServerFilters.maxKk,
    itemNames: debouncedServerFilters.itemNames.length > 0 ? debouncedServerFilters.itemNames : undefined,
    apelidos: debouncedServerFilters.apelidos.length > 0 ? debouncedServerFilters.apelidos : undefined,
    propostasOnly: debouncedServerFilters.propostasOnly || undefined,
    page: currentPage,
    pageSize: PAGE_SIZE,
    excludeDestaque: !showDestaques && config?.carrossel_destaque === "true" ? true : undefined,
    viewerProfileId: session?.perfil_id || undefined,
  });

  const anunciosBase = anunciosResult?.anuncios || [];
  const serverTotal = anunciosResult?.total || 0;

  // Fetch featured items separately for carousel (same filters as main list)
  const carouselFilters = useMemo(() => ({
    tipo: tipoFilter || undefined,
    excludeTipos: ["personagem"],
    servidores: debouncedServerFilters.servidores.length > 0 ? debouncedServerFilters.servidores : undefined,
    tiers: debouncedServerFilters.tiers.length > 0 ? debouncedServerFilters.tiers : undefined,
    minReais: debouncedServerFilters.minReais,
    maxReais: debouncedServerFilters.maxReais,
    minCoins: debouncedServerFilters.minCoins,
    maxCoins: debouncedServerFilters.maxCoins,
    minKk: debouncedServerFilters.minKk,
    maxKk: debouncedServerFilters.maxKk,
  }), [tipoFilter, debouncedServerFilters]);

  const { data: featuredResult } = useQuery({
    queryKey: ["anuncios_featured", carouselFilters, session?.perfil_id],
    queryFn: async () => {
      const result = await fetchPublicAnunciosPaginated({
        destaqueOnly: true,
        viewerProfileId: session?.perfil_id || undefined,
        ...carouselFilters,
      });
      return result.anuncios;
    },
    staleTime: 60_000,
  });
  const featuredAnuncios = featuredResult || [];

  const handleAnuncioActionRefresh = useCallback(() => {
    refetchAnuncios();
    refetchPersonagens();
    queryClient.invalidateQueries({ queryKey: ["my_all_ads"] });
    queryClient.invalidateQueries({ queryKey: ["my-anuncio-ids"] });
    queryClient.invalidateQueries({ queryKey: ["anuncios_featured"] });
    queryClient.invalidateQueries({ queryKey: ["anuncios_filter_options"] });
  }, [refetchAnuncios, refetchPersonagens, queryClient]);

  // Realtime: auto-refresh when anuncios are inserted or updated
  useEffect(() => {
    const channel = supabase
      .channel("anuncios-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "anuncios" },
        () => handleAnuncioActionRefresh(),
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "anuncios" },
        () => handleAnuncioActionRefresh(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [handleAnuncioActionRefresh]);

  // Fetch ALL user's ads (used for pending merge AND "Meus Anúncios" toggle)
  const { data: myAllAds } = useQuery({
    queryKey: ["my_all_ads", session?.contato, session?.token],
    queryFn: async () => {
      if (!session?.contato || !session?.token) return [];
      const { data } = await invokeWithSessionGuard(() =>
        supabase.functions.invoke("client-anuncios", {
          body: { contato: session.contato, token: session.token, perfil_id: session.perfil_id },
        })
      );
      if (!data?.anuncios) return [];
      return data.anuncios as any[];
    },
    enabled: !!session?.contato && !!session?.token,
    staleTime: 60_000,
  });

  // Owner-only ads merged into the public list: pending approval, awaiting payment, and paused.
  // The server excludes these from PUBLIC_VISIBLE_STATUSES, so they're invisible to others.
  const myPendingAds = useMemo(() => {
    if (!myAllAds) return [];
    return myAllAds.filter(
      (a: any) =>
        a.tipo !== "personagem" &&
        (a.status === "pendente" ||
          a.status === "aguardando_pagamento" ||
          a.status === "pausado"),
    );
  }, [myAllAds]);

  // Merge the owner's own personagem ads (freshest status + paused/pending visibility)
  // into the public personagem list, mirroring how item cards stay visible to their owner.
  const personagemAnunciosMerged = useMemo(() => {
    const myCharAds = (myAllAds || []).filter(
      (a: any) => a.tipo === "personagem" && a.personagem,
    );
    const enrichOwner = (a: any) => ({
      ...a,
      tem_whatsapp: a.tem_whatsapp ?? true,
      tem_email: a.tem_email ?? false,
      apelido: a.apelido || session?.apelido || session?.nome || null,
      anunciante_id: a.anunciante_id || session?.perfil_id || null,
      anunciante_avatar_url: a.anunciante_avatar_url || session?.avatar_url || null,
      preferencias_contato: a.preferencias_contato || ["plataforma"],
    });
    const myMap = new Map<string, any>();
    for (const a of myCharAds) myMap.set(a.id, enrichOwner(a));

    // Replace public entries with the owner's fresher copy (e.g. just paused)
    const base = (personagemAnuncios || []).map((a: any) =>
      myMap.has(a.id) ? myMap.get(a.id) : a,
    );
    const baseIds = new Set(base.map((a: any) => a.id));

    const OWNER_MERGEABLE = new Set(["pendente", "aguardando_pagamento", "pausado"]);
    const nowMs = Date.now();
    const toMerge = [...myMap.values()].filter((a: any) => {
      if (baseIds.has(a.id)) return false;
      if (!OWNER_MERGEABLE.has(a.status)) return false;
      if (a.expira_em && new Date(a.expira_em).getTime() <= nowMs) return false;
      return true;
    });
    return toMerge.length === 0 ? base : [...toMerge, ...base];
  }, [personagemAnuncios, myAllAds, session]);

  const filteredPersonagemAnuncios = useMemo(
    () => filterPersonagemAnuncios(personagemAnunciosMerged, personagemFilters),
    [personagemAnunciosMerged, personagemFilters],
  );



  const { data: filterOptionsBase = [] } = useQuery({
    queryKey: [
      "anuncios_filter_options",
      tipoFilter,
      debouncedServerFilters.servidores,
      debouncedServerFilters.tiers,
      debouncedServerFilters.minReais,
      debouncedServerFilters.maxReais,
      debouncedServerFilters.minCoins,
      debouncedServerFilters.maxCoins,
      debouncedServerFilters.minKk,
      debouncedServerFilters.maxKk,
      showDestaques,
      config?.carrossel_destaque,
    ],
    queryFn: async () => {
      const pageSize = 200; // server caps pageSize at 200
      const maxPages = 100; // up to 20.000 anúncios
      const anuncios: Anuncio[] = [];

      for (let page = 1; page <= maxPages; page += 1) {
        const result = await fetchPublicAnunciosPaginated({
          tipo: tipoFilter || undefined,
          excludeTipos: ["personagem"],
          servidores: debouncedServerFilters.servidores.length > 0 ? debouncedServerFilters.servidores : undefined,
          tiers: debouncedServerFilters.tiers.length > 0 ? debouncedServerFilters.tiers : undefined,
          minReais: debouncedServerFilters.minReais,
          maxReais: debouncedServerFilters.maxReais,
          minCoins: debouncedServerFilters.minCoins,
          maxCoins: debouncedServerFilters.maxCoins,
          minKk: debouncedServerFilters.minKk,
          maxKk: debouncedServerFilters.maxKk,
          page,
          pageSize,
          excludeDestaque: !showDestaques && config?.carrossel_destaque === "true" ? true : undefined,
        });

        if (result.anuncios.length === 0) break;
        anuncios.push(...(result.anuncios as Anuncio[]));
        if (result.anuncios.length < pageSize) break;
      }

      return anuncios;
    },
    enabled: !showMyAds && serverTotal > 0,
    staleTime: 30_000,
  });

  const filterOptionsAnuncios = useMemo(() => {
    if (showMyAds && myAllAds && myAllAds.length > 0) {
      return myAllAds
        .filter((a: any) => a.tipo !== "personagem")
        .map((a: any) => ({
        ...a,
        tem_whatsapp: a.tem_whatsapp ?? true,
        tem_email: a.tem_email ?? false,
        apelido: a.apelido || session?.apelido || session?.nome || null,
        anunciante_id: a.anunciante_id || session?.perfil_id || null,
        anunciante_avatar_url: a.anunciante_avatar_url || session?.avatar_url || null,
        preferencias_contato: a.preferencias_contato || ["plataforma"],
        item_custom: a.item_custom ?? false,
      })) as Anuncio[];
    }

    // Merge base (full list, slower) with anunciosBase (first page, fast) so the
    // combobox is populated immediately while the full list is being fetched.
    // Deduplicate by id, prioritizing entries from filterOptionsBase.
    const baseList = filterOptionsBase.length > 0 ? filterOptionsBase : (anunciosBase as Anuncio[]);
    const baseIds = new Set(baseList.map((a) => a.id));

    const pending = (myPendingAds || [])
      .filter((a: any) => !baseIds.has(a.id))
      .map((a: any) => ({
        ...a,
        tem_whatsapp: true,
        tem_email: false,
        apelido: session?.apelido || session?.nome || null,
        anunciante_id: session?.perfil_id || null,
        anunciante_avatar_url: session?.avatar_url || null,
        preferencias_contato: ["plataforma"],
        item_custom: false,
      })) as Anuncio[];

    return [...pending, ...baseList];
  }, [filterOptionsBase, anunciosBase, myPendingAds, myAllAds, showMyAds, session]);

  // When showMyAds is active, use all user's ads directly instead of server-paginated data
  const anuncios = useMemo(() => {
    if (showMyAds && myAllAds && myAllAds.length > 0) {
      // Use all user's ads as the data source (no server pagination needed)
      return myAllAds
        .filter((a: any) => a.tipo !== "personagem")
        .map((a: any) => ({
        ...a,
        tem_whatsapp: a.tem_whatsapp ?? true,
        tem_email: a.tem_email ?? false,
        apelido: a.apelido || session?.apelido || session?.nome || null,
        anunciante_id: a.anunciante_id || session?.perfil_id || null,
        anunciante_avatar_url: a.anunciante_avatar_url || session?.avatar_url || null,
        preferencias_contato: a.preferencias_contato || ["plataforma"],
        item_custom: a.item_custom ?? false,
      })) as Anuncio[];
    }

    // Build a map of owner's ads (from myAllAds) for quick lookup.
    // Owner ads have the freshest status (e.g. just paused/resumed),
    // so they override the server-paginated version when both exist.
    const myAdsMap = new Map<string, any>();
    const enrichOwnerAd = (a: any) => ({
      ...a,
      tem_whatsapp: a.tem_whatsapp ?? true,
      tem_email: a.tem_email ?? false,
      apelido: a.apelido || session?.apelido || session?.nome || null,
      anunciante_id: a.anunciante_id || session?.perfil_id || null,
      anunciante_avatar_url: a.anunciante_avatar_url || session?.avatar_url || null,
      preferencias_contato: a.preferencias_contato || ["plataforma"],
      item_custom: a.item_custom ?? false,
    });
    for (const a of myAllAds || []) {
      if (a.tipo === "personagem") continue;
      myAdsMap.set(a.id, enrichOwnerAd(a));
    }

    // Replace base entries with fresher owner data when available
    const base = anunciosBase.map((a) =>
      myAdsMap.has(a.id) ? (myAdsMap.get(a.id) as Anuncio) : a,
    );
    const baseIds = new Set(base.map((a) => a.id));

    // Merge owner ads missing from server response (paused, pending, awaiting payment).
    // IMPORTANT: only merge statuses that are legitimately "owner-only visible" — never merge
    // expired/sold/closed/rejected ads, and never merge an "aprovado" whose expira_em already
    // passed (those would render as ghost cards in the feed with all actions disabled).
    const OWNER_MERGEABLE_STATUSES = new Set(["pendente", "aguardando_pagamento", "pausado"]);
    const nowMs = Date.now();
    const ownerAdsToMerge = [...myAdsMap.values()]
      .filter((a: any) => {
        if (baseIds.has(a.id)) return false;
        if (!OWNER_MERGEABLE_STATUSES.has(a.status)) return false;
        if (a.expira_em && new Date(a.expira_em).getTime() <= nowMs) return false;
        return true;
      }) as Anuncio[];

    // Merge interesse-matching ads that aren't already in the current page so
    // the priority sort can promote them right after the user's own ads in
    // intermediation. Without this merge, matches that live on other server
    // pages would never appear at the top.
    const ownerIds = new Set(ownerAdsToMerge.map((a) => a.id));
    const interesseExtras: Anuncio[] = [];
    const pushExtra = (a: any) => {
      if (!baseIds.has(a.id) && !ownerIds.has(a.id)) {
        interesseExtras.push(a as Anuncio);
        ownerIds.add(a.id);
      }
    };
    for (const m of interessesMatches) {
      for (const a of (m.anuncios || []) as any[]) pushExtra(a);
    }
    for (const a of (interessesSummary?.itemAnuncios || []) as any[]) pushExtra(a);

    if (ownerAdsToMerge.length === 0 && interesseExtras.length === 0) return base;
    return [...ownerAdsToMerge, ...interesseExtras, ...base];
  }, [anunciosBase, myPendingAds, myAllAds, showMyAds, session, interessesMatches, interessesSummary]);

  // Carousel config
  const carouselRotationTime = config?.carrossel_tempo_rotacao ? parseFloat(config.carrossel_tempo_rotacao) : 4;
  const carouselRotateAll = config?.carrossel_rotacionar_todos === "true";
  const carouselFitToScreen = config?.carrossel_ajuste_tela === "true";

  const itemIds = useMemo(() => [...new Set((anuncios || []).map((a) => a.item_id).filter(Boolean))], [anuncios]);

  const { data: itemDetails } = useQuery({
    queryKey: ["tibia_items_details", itemIds],
    queryFn: async () => {
      if (itemIds.length === 0) return {};
      const map: Record<string, any> = {};
      const BATCH = 80;
      for (let i = 0; i < itemIds.length; i += BATCH) {
        const batch = (itemIds as string[]).slice(i, i + BATCH);
        const { data } = await supabase
          .from("tibia_items")
          .select("id, name, upgradeclass, custom, image_url, levelrequired, attack, defense, primarytype")
          .in("id", batch);
        for (const item of data || []) map[item.id] = item;
      }
      return map;
    },
    enabled: itemIds.length > 0,
  });

  const { data: allTibiaItems } = useQuery({
    queryKey: ["all_tibia_items_names"],
    queryFn: async () => {
      const { data } = await supabase.from("tibia_items").select("name, image_url").order("name");
      return (data || []) as Array<{ name: string; image_url: string | null }>;
    },
    enabled: showAllItems,
  });

  const isExpiredForFilter = (anuncio: Anuncio, now = new Date()) => {
    // Cancelled ads without payment confirmed should be ignored from Finalizados
    if (anuncio.status === "encerrado" && !(anuncio as any).taxa_paga) return false;
    if (anuncio.status === "vendido" || anuncio.status === "expirado" || anuncio.status === "pagamento_expirado" || anuncio.status === "encerrado")
      return true;
    if (anuncio.status === "pendente" || anuncio.status === "aguardando_pagamento") return false;
    return !!anuncio.expira_em && new Date(anuncio.expira_em) <= now;
  };

  // Helper: check if anuncio belongs to current user using all available signals
  const isOwnedByMe = (a: Anuncio): boolean => {
    if (!session) return false;
    // 1. Server-resolved owned IDs (most reliable — full contact resolution)
    if (myAnuncioIds && myAnuncioIds.has(a.id)) return true;
    // 2. Profile ID match (from public-anuncios resolved anunciante_id)
    if (session.perfil_id && (a as any).anunciante_id && session.perfil_id === (a as any).anunciante_id) return true;
    return false;
  };

  // Apply filters to all anuncios (carousel uses the same filtered list)
  const applyFilters = (list: Anuncio[], opts?: { forCarousel?: boolean }) => {
    let result = list;

    // Filter paused/pending/aguardando_pagamento/vendido items: only show to their owner (vendido also visible when showExpired)
    // Owner's pending destaque ads belong to the carousel (not the main list) when the carousel is active.
    const carouselActive = !showDestaques && !showExpired && config?.carrossel_destaque === "true";
    const isForCarousel = opts?.forCarousel === true;
    const nowMs = Date.now();
    // An ad whose destaque hasn't started yet (scheduled mirror) still belongs in the regular list.
    const isScheduledDestaque = (a: any) => {
      const di = a?.destaque_inicio ? new Date(a.destaque_inicio).getTime() : 0;
      return !!a?.destaque && di > nowMs;
    };
    result = result.filter((a) => {
      if (a.status === "pagamento_expirado") {
        return showExpired;
      }
      if (a.status === "pendente" || a.status === "aguardando_pagamento") {
        if (showExpired || !isOwnedByMe(a)) return false;
        // While the carousel is on, hide the owner's pending destaque ads from the main list — they show in the carousel.
        if (!isForCarousel && carouselActive && a.destaque && !isScheduledDestaque(a)) return false;
        return true;
      }
      if (a.status === "pausado") {
        if (!isOwnedByMe(a)) return false;
        // While the carousel is on, hide the owner's paused destaque ads from the main list — they show in the carousel.
        if (!isForCarousel && carouselActive && a.destaque && !isScheduledDestaque(a)) return false;
        return true;
      }
      if (a.status === "vendido") {
        return showExpired || isOwnedByMe(a);
      }
      if (a.status === "encerrado") {
        // Hide cancelled ads without payment from all views
        if (!(a as any).taxa_paga) return false;
        return showExpired;
      }
      // When carousel is active, hide destaque ads from the main list (they appear in the carousel)
      // Exception: scheduled-destaque ads still belong in the main list until their destaque period starts.
      if (!isForCarousel && carouselActive && a.destaque && !isScheduledDestaque(a)) return false;
      return true;
    });

    // Filter by tipo (compra/venda) — when both are off, show nothing
    if (!filterCompra && !filterVenda) {
      return [];
    }
    if (!filterCompra) {
      result = result.filter((a) => a.tipo !== "compra");
    }
    if (!filterVenda) {
      result = result.filter((a) => a.tipo !== "venda");
    }

    // Filter by expiration status
    const now = new Date();
    if (showExpired) {
      // Only show truly expired items; vendidos count as expired and pending/payment items stay hidden
      result = result.filter((a) => isExpiredForFilter(a, now));
    } else {
      result = result.filter((a) => !isExpiredForFilter(a, now));
    }

    if (selectedItems.length > 0) {
      result = result.filter((a) => selectedItems.includes(a.item_nome));
    }
    if (servidorFilter.length > 0) {
      result = result.filter((a) => a.servidor && servidorFilter.includes(a.servidor));
    }
    if (tierFilter.length > 0) {
      result = result.filter(
        (a) => (tierFilter.includes(0) && a.tier == null) || (a.tier != null && tierFilter.includes(a.tier)),
      );
    }
    if (minReais) {
      const v = parseFloat(minReais);
      if (!isNaN(v)) result = result.filter((a) => a.preco >= v);
    }
    if (maxReais) {
      const v = parseFloat(maxReais);
      if (!isNaN(v)) result = result.filter((a) => a.preco <= v);
    }
    if (minCoins) {
      const v = parseFloat(minCoins);
      if (!isNaN(v)) result = result.filter((a) => (a.preco_coins || 0) >= v);
    }
    if (maxCoins) {
      const v = parseFloat(maxCoins);
      if (!isNaN(v)) result = result.filter((a) => (a.preco_coins || 0) <= v);
    }
    if (minKk) {
      const v = parseFloat(minKk);
      if (!isNaN(v)) result = result.filter((a) => (a.preco_kk || 0) >= v);
    }
    if (maxKk) {
      const v = parseFloat(maxKk);
      if (!isNaN(v)) result = result.filter((a) => (a.preco_kk || 0) <= v);
    }
    if (showMyList) {
      const favs = getFavorites();
      result = result.filter((a) => favs.includes(a.id));
    }
    if (showDestaques) {
      result = result.filter((a) => a.destaque);
    }
    if (filterPropostas) {
      result = result.filter(
        (a) => a.preco === 0 && (!a.preco_coins || a.preco_coins === 0) && (!a.preco_kk || a.preco_kk === 0),
      );
    }
    if (advertiserFilter.length > 0) {
      result = result.filter((a) => (a as any).apelido && advertiserFilter.includes((a as any).apelido));
    }
    if (showMyAds && session) {
      result = result.filter((a) => isOwnedByMe(a));
    }
    return result;
  };

  // Compute display priority for an ad. Lower number = appears first.
  // 0: user's own ads currently in intermediation
  // 0.5: ads where the user solicited intermediação (interessado)
  // 1: ads matching the user's "itens de interesse"
  // 2: user's own ads awaiting payment (new ad)
  // 2.5: user's own ads awaiting approval (pendente) - after awaiting payment
  // 3: user's own ads with renewal payment pending
  // 4: user's own paused ads
  // 5: everything else (then sorted by created_at desc)
  const getSortPriority = useCallback(
    (a: Anuncio): number => {
      const ownedByMe = isOwnedByMe(a);
      if (
        ownedByMe &&
        (!!intermediacaoByAnuncio[a.id] || publicIntermediatingAnuncios.has(a.id))
      ) return 0;
      // Ads where user solicited intermediação (user is the interessado, not the owner)
      if (!ownedByMe && !!intermediacaoByAnuncio[a.id]) return 0.5;
      // Any ad in active intermediação (visible to everyone, including non-logged users)
      if (publicIntermediatingAnuncios.has(a.id)) return 0.8;
      if (!isOwnedByMe(a) && (interesseAnuncioIds.has(a.id) || (a.tipo === "venda" && interesseItemNames.has((a.item_nome || "").toLowerCase())))) return 1;
      if (ownedByMe && a.status === "aguardando_pagamento") return 2;
      if (ownedByMe && a.status === "pendente") return 2.5;
      if (ownedByMe && (a as any).renovacao_pendente) return 3;
      if (ownedByMe && a.status === "pausado") return 4;
      return 5;
    },
    [intermediacaoByAnuncio, publicIntermediatingAnuncios, interesseAnuncioIds, interesseItemNames, session, myAnuncioIds],
  );

  const isInteresseAd = useCallback(
    (a: Anuncio): boolean =>
      !isOwnedByMe(a) && (
        interesseAnuncioIds.has(a.id) ||
        (a.tipo === "venda" && interesseItemNames.has((a.item_nome || "").toLowerCase()))
      ),
    [interesseAnuncioIds, interesseItemNames, session, myAnuncioIds],
  );

  const sortByPriority = useCallback(
    (list: Anuncio[]): Anuncio[] => {
      return [...list].sort((a, b) => {
        const pa = getSortPriority(a);
        const pb = getSortPriority(b);
        if (pa !== pb) return pa - pb;
        // Within same priority, newer first
        const ta = new Date((a as any).aprovado_em || a.created_at).getTime();
        const tb = new Date((b as any).aprovado_em || b.created_at).getTime();
        return tb - ta;
      });
    },
    [getSortPriority],
  );

  const filteredAnuncios = useMemo(() => {
    const all = applyFilters(anuncios || []);

    // When showExpired, keep only expired items and prioritize featured ones
    if (showExpired) {
      return [...all].sort((a, b) => Number(b.destaque) - Number(a.destaque));
    }

    return sortByPriority(all);
  }, [
    anuncios,
    selectedItems,
    servidorFilter,
    tierFilter,
    minReais,
    maxReais,
    minCoins,
    maxCoins,
    minKk,
    maxKk,
    showMyList,
    showDestaques,
    showExpired,
    config?.carrossel_destaque,
    advertiserFilter,
    showMyAds,
    session,
    myAnuncioIds,
    filterPropostas,
    filterCompra,
    filterVenda,
    sortByPriority,
  ]);

  // Helper to group anuncios into single/batch items (each pending batch = 1 visual slot)
  type GroupedItem = { type: "single"; anuncio: Anuncio } | { type: "batch"; loteId: string; anuncios: Anuncio[] };
  const groupAnuncios = useCallback((items: Anuncio[]): GroupedItem[] => {
    const result: GroupedItem[] = [];
    const seenLotes = new Set<string>();
    for (const anuncio of items) {
      const isPendingBatch =
        anuncio.lote_id && (anuncio.status === "pendente" || anuncio.status === "aguardando_pagamento");
      if (isPendingBatch) {
        if (seenLotes.has(anuncio.lote_id!)) continue;
        seenLotes.add(anuncio.lote_id!);
        const batchItems = items.filter(
          (a) => a.lote_id === anuncio.lote_id && (a.status === "pendente" || a.status === "aguardando_pagamento"),
        );
        result.push({ type: "batch", loteId: anuncio.lote_id!, anuncios: batchItems });
      } else {
        result.push({ type: "single", anuncio });
      }
    }
    return result;
  }, []);

  // Group ALL filtered anuncios (before pagination) so we can paginate by groups
  const allGroupedFilteredAnuncios = useMemo(() => groupAnuncios(filteredAnuncios), [filteredAnuncios, groupAnuncios]);

  // When showExpired or showMyAds, server total doesn't reflect client-side filtering, so use client-filtered count.
  // Otherwise add owner's merged-in private ads (pending/awaiting payment/paused) that actually survived
  // the local filters (e.g. exclude expired pausado, server/tier mismatches) — counting them blindly inflates
  // the badge with ads that never render.
  const myPendingIdSet = useMemo(
    () => new Set((myPendingAds || []).map((a: any) => a.id)),
    [myPendingAds],
  );
  const visibleOwnerMergedCount = useMemo(
    () => filteredAnuncios.filter((a) => myPendingIdSet.has(a.id)).length,
    [filteredAnuncios, myPendingIdSet],
  );
  // Visual total: each pending batch counts as 1 slot, not its individual item count
  const visualDisplayTotal = useMemo(() => {
    if (showMyAds || showExpired || showMyList) {
      // For client-side modes, paginate by groups (each batch = 1)
      return allGroupedFilteredAnuncios.length;
    }
    // For public list: count visible items treating batches as 1
    const seenLotes = new Set<string>();
    let count = 0;
    for (const a of filteredAnuncios) {
      if (a.lote_id && (a.status === "pendente" || a.status === "aguardando_pagamento")) {
        if (seenLotes.has(a.lote_id)) continue;
        seenLotes.add(a.lote_id);
        count += 1;
      } else {
        count += 1;
      }
    }
    return count;
  }, [allGroupedFilteredAnuncios, showMyAds, showExpired, showMyList, filteredAnuncios]);

  const displayTotal = visualDisplayTotal;
  const totalPages = useMemo(() => {
    if (showMyAds || showExpired || showMyList) {
      return Math.max(1, Math.ceil(allGroupedFilteredAnuncios.length / PAGE_SIZE));
    }
    return Math.max(1, Math.ceil((serverTotal + visibleOwnerMergedCount) / PAGE_SIZE));
  }, [showMyAds, showExpired, showMyList, allGroupedFilteredAnuncios, serverTotal, visibleOwnerMergedCount]);

  const comboboxAnuncios = useMemo(() => {
    let result = filterOptionsAnuncios || [];

    // Filter by status visibility (same logic as applyFilters)
    result = result.filter((a) => {
      if (a.status === "pagamento_expirado") return showExpired;
      if (a.status === "pendente" || a.status === "aguardando_pagamento") return !showExpired && isOwnedByMe(a);
      if (a.status === "pausado") return isOwnedByMe(a);
      if (a.status === "vendido") return showExpired || isOwnedByMe(a);
      if (a.status === "encerrado") {
        if (!(a as any).taxa_paga) return false;
        return showExpired;
      }
      return true;
    });

    // Filter compra/venda
    if (!filterCompra && !filterVenda) return [];
    if (!filterCompra) result = result.filter((a) => a.tipo !== "compra");
    if (!filterVenda) result = result.filter((a) => a.tipo !== "venda");

    // Filter by expiration
    const now = new Date();
    if (showExpired) {
      result = result.filter((a) => isExpiredForFilter(a, now));
    } else {
      result = result.filter((a) => !isExpiredForFilter(a, now));
    }

    // Apply other filters (server, tier) but NOT selectedItems
    if (servidorFilter.length > 0) result = result.filter((a) => a.servidor && servidorFilter.includes(a.servidor));
    if (tierFilter.length > 0) result = result.filter((a) => (tierFilter.includes(0) && a.tier == null) || (a.tier != null && tierFilter.includes(a.tier)));

    return result;
  }, [filterOptionsAnuncios, showExpired, filterCompra, filterVenda, servidorFilter, tierFilter, session, myAnuncioIds]);

  // When using client-side data (showMyAds or showExpired), paginate by groups (each batch = 1)
  const paginatedAnuncios = useMemo(() => {
    if (showMyAds || showExpired) {
      const start = (currentPage - 1) * PAGE_SIZE;
      // Get the anuncios from the paginated groups
      const paginatedGroups = allGroupedFilteredAnuncios.slice(start, start + PAGE_SIZE);
      const result: Anuncio[] = [];
      for (const group of paginatedGroups) {
        if (group.type === "single") {
          result.push(group.anuncio);
        } else {
          result.push(...group.anuncios);
        }
      }
      return result;
    }
    return filteredAnuncios;
  }, [filteredAnuncios, showMyAds, showExpired, currentPage, allGroupedFilteredAnuncios]);

  // Group batch ads (pendente/aguardando_pagamento with lote_id) into stacks
  const groupedFilteredAnuncios = useMemo((): GroupedItem[] => {
    return groupAnuncios(paginatedAnuncios);
  }, [paginatedAnuncios, groupAnuncios]);

  // Build a map of ALL items per lote (across full dataset, regardless of destaque/filter status)
  // so the QR overlay can show every thumbnail of the batch with a destaque marker.
  const loteFullItemsMap = useMemo(() => {
    const map = new Map<string, { item_nome: string; destaque: boolean; id: string }[]>();
    for (const a of anuncios) {
      if (!a.lote_id) continue;
      if (a.status !== "pendente" && a.status !== "aguardando_pagamento") continue;
      const arr = map.get(a.lote_id) || [];
      arr.push({ item_nome: a.item_nome, destaque: !!(a as any).destaque, id: a.id });
      map.set(a.lote_id, arr);
    }
    return map;
  }, [anuncios]);

  // Trim trailing items to keep the grid rows complete (5 cards per row on desktop).
  // On the last page, keep the remainder so nothing is hidden.
  const displayedGroupedAnuncios = useMemo((): GroupedItem[] => {
    const ROW = 5;
    const isLastPage = currentPage >= totalPages;
    if (isLastPage) return groupedFilteredAnuncios;
    const len = groupedFilteredAnuncios.length;
    const trimmed = Math.floor(len / ROW) * ROW;
    return trimmed > 0 ? groupedFilteredAnuncios.slice(0, trimmed) : groupedFilteredAnuncios;
  }, [groupedFilteredAnuncios, currentPage, totalPages]);

  const filteredForCarousel = useMemo(() => {
    if (showExpired) return [];
    const now = new Date();
    const favs = showMyList ? getFavorites() : null;
    const preFiltered = featuredAnuncios.filter((a: any) => {
      // Owner can always see their own paused/pending/awaiting payment/sold ads
      const owned = isOwnedByMe(a);
      if (a.status === "pausado") return owned;
      if (a.status === "pendente" || a.status === "aguardando_pagamento") return owned;
      if (a.status === "vendido") return owned;
      // Non-owner expired ads are hidden
      if (a.expira_em && new Date(a.expira_em) <= now) return false;
      if (favs && !favs.includes(a.id)) return false;
      return true;
    });
    // Aplicar todos os filtros ativos do usuário (tipo, servidor, tier, preço, item, anunciante, etc.)
    const base = applyFilters(preFiltered, { forCarousel: true });

    // Merge owner's own pending/paused destaque ads so they show in the carousel
    const baseIds = new Set(base.map((a: any) => a.id));
    const ownerExtraDestaques: any[] = [];

    // Pending ads (awaiting approval/payment)
    if (myPendingAds && myPendingAds.length > 0) {
      for (const a of myPendingAds) {
        if ((a as any).destaque && !baseIds.has(a.id) && (!favs || favs.includes(a.id))) {
          ownerExtraDestaques.push(a);
          baseIds.add(a.id);
        }
      }
    }

    // Owner destaque ads not yet in server response (paused, just resumed, etc.)
    if (myAllAds && myAllAds.length > 0) {
      for (const a of myAllAds) {
        if ((a as any).destaque && !baseIds.has(a.id) && (!favs || favs.includes(a.id))) {
          ownerExtraDestaques.push(a);
          baseIds.add(a.id);
        }
      }
    }

    if (ownerExtraDestaques.length > 0) {
      const mapped = ownerExtraDestaques.map((a: any) => ({
        ...a,
        tem_whatsapp: a.tem_whatsapp ?? true,
        tem_email: a.tem_email ?? false,
        apelido: a.apelido || session?.apelido || session?.nome || null,
        anunciante_id: a.anunciante_id || session?.perfil_id || null,
        anunciante_avatar_url: a.anunciante_avatar_url || session?.avatar_url || null,
        preferencias_contato: a.preferencias_contato || ["plataforma"],
        item_custom: a.item_custom ?? false,
      }));
      const ownerFiltered = applyFilters(mapped as any, { forCarousel: true });
      return sortByPriority([...ownerFiltered, ...base]);
    }
    return sortByPriority(base);
  }, [featuredAnuncios, showExpired, showMyList, session, myAnuncioIds, myPendingAds, myAllAds, sortByPriority, applyFilters]);

  const handleApelidoClick = useCallback(async (apelido: string) => {
    const { data } = await supabase.from("clientes_perfil_public").select("id").eq("apelido", apelido).maybeSingle();
    if (data?.id) setProfileDialogId(data.id);
  }, []);

  const isExpired = (anuncio: any) => !!(anuncio.expira_em && new Date(anuncio.expira_em) <= new Date());

  const handleCardClick = useCallback(async (anuncio: any) => {
    // Track view only if not the owner
    const isOwner = checkIsOwner(session, anuncio as any, myAnuncioIds);
    if (!isOwner) {
      const fp = getSessionFingerprint();
      supabase.functions
        .invoke("track-view", {
          body: {
            anuncio_id: anuncio.id,
            session_fingerprint: fp,
            contato: session?.contato || null,
            token: session?.token || null,
          },
        })
        .catch(() => {});
    }

    // Fetch full item details on-demand only when modal opens
    let details: any = null;
    if (anuncio.item_id) {
      const cached = itemDetails?.[anuncio.item_id];
      // If cached has full details (has attrib key), use it; otherwise fetch
      if (cached && 'attrib' in cached) {
        details = cached;
      } else {
        const { data: fullItem } = await supabase
          .from("tibia_items")
          .select("*")
          .eq("id", anuncio.item_id)
          .single();
        details = fullItem;
      }
    }

    setDetailItem({
      anuncioId: anuncio.id,
      itemNome: anuncio.item_nome,
      tipo: anuncio.tipo,
      preco: anuncio.preco,
      moeda: anuncio.moeda,
      precoCoins: anuncio.preco_coins,
      precoKk: anuncio.preco_kk,
      servidor: anuncio.servidor,
      destaque: anuncio.destaque,
      descricao: anuncio.descricao,
      level: details?.levelrequired,
      attack: details?.attack,
      defense: details?.defense,
      primarytype: details?.primarytype,
      weight: details?.weight,
      slot: details?.slot,
      vocation: details?.vocrequired,
      hands: details?.hands,
      weapontype: details?.weapontype,
      objectclass: details?.objectclass,
      elementalbond: details?.elementalbond,
      marketable: details?.marketable,
      pickupable: details?.pickupable,
      value: details?.value,
      upgradeclass: details?.upgradeclass,
      imbueslots: details?.imbueslots,
      npcprice: details?.npcprice,
      npcvalue: details?.npcvalue,
      notes: details?.notes,
      buyfrom: details?.buyfrom,
      sellto: details?.sellto,
      implemented: details?.implemented,
      stackable: details?.stackable,
      walkable: details?.walkable,
      immobile: details?.immobile,
      history: details?.history,
      usable: details?.usable,
      droppedby: details?.droppedby,
      imageUrl: details?.image_url,
      range: details?.range,
      atk_mod: details?.atk_mod,
      hit_mod: details?.hit_mod,
      attrib: details?.attrib,
      resist: details?.resist,
      earth_attack: details?.earth_attack,
      energy_attack: details?.energy_attack,
      death_attack: details?.death_attack,
      fire_attack: details?.fire_attack,
      ice_attack: details?.ice_attack,
      holy_attack: details?.holy_attack,
      defensemod: details?.defensemod,
      tier: anuncio.tier,
      expiraEm: anuncio.expira_em || null,
      expired: isExpired(anuncio),
      anuncianteId: (anuncio as any).anunciante_id || null,
      anuncianteAvatarUrl: (anuncio as any).anunciante_avatar_url || null,
      preferenciasContato: (anuncio as any).preferencias_contato || ["plataforma"],
      temWhatsapp: !!(anuncio as any).tem_whatsapp,
      temEmail: !!(anuncio as any).tem_email,
      apelido: (anuncio as any).apelido || null,
      anuncioStatus: anuncio.status || "aprovado",
      renovacaoPendente: !!(anuncio as any).renovacao_pendente,
      isIntermediating: publicIntermediatingAnuncios.has(anuncio.id),
    });
  }, [session, myAnuncioIds, itemDetails, publicIntermediatingAnuncios]);

  // Personagem cards open their own combined grid dialog; here we only track the view.
  const handlePersonagemView = useCallback((anuncio: any) => {
    const isOwner = checkIsOwner(session, anuncio as any, myAnuncioIds);
    if (isOwner) return;
    const fp = getSessionFingerprint();
    supabase.functions
      .invoke("track-view", {
        body: {
          anuncio_id: anuncio.id,
          session_fingerprint: fp,
          contato: session?.contato || null,
          token: session?.token || null,
        },
      })
      .catch(() => {});
  }, [session, myAnuncioIds]);

  return (
    <>
      <div
        className="h-screen flex flex-col overflow-hidden bg-background bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url(${stoneBg})` }}
      >
        <div className="flex-1 flex flex-col overflow-hidden min-h-0 bg-background/85 backdrop-blur-sm">
          {/* Header */}
          <header
            className="shrink-0 relative z-50 py-3 sm:py-6 border-b border-border/50 bg-stone-dark/80 backdrop-blur-md animate-fade-in"
            style={{ background: "linear-gradient(180deg, hsl(20 6% 10% / 0.95) 0%, hsl(20 6% 12% / 0.85) 100%)" }}
          >
            <div className="container max-w-5xl mx-auto px-2 sm:px-4 flex items-center justify-between gap-1 sm:gap-2">
              <button
                type="button"
                onClick={() => {
                  if (isMobile && filtersOpen) setFiltersOpen(false);
                }}
                className="flex items-center gap-1 sm:gap-2 cursor-pointer bg-transparent border-0 p-0"
                aria-label="Início"
              >
                <img src={logo} alt="RPG Coins" width={40} height={40} {...({ fetchpriority: "high" } as any)} decoding="async" className="h-7 w-auto sm:h-10 object-contain rounded-full" />
                <div className="flex flex-col">
                  <h1 className="font-pixel text-[7px] sm:text-base text-primary gold-text-glow tracking-wider leading-none">
                    <span className="inline sm:hidden">MARKETPLACE</span>
                    <span className="hidden sm:inline">RPG COINS - MARKETPLACE</span>
                  </h1>
                </div>
              </button>

              {/* Container de botões */}
              <div className="flex items-center gap-1 sm:gap-2 flex-nowrap">
                {/* Botão ANUNCIAR */}
                <button
                  onClick={() => requireLogin(() => {
                    if (topTab === "coins") setChooseTypeOpen(true);
                    else if (topTab === "personagens") setCreatePersonagemOpen(true);
                    else setCreateOpen(true);
                  })}
                  className="tibia-btn rounded-sm flex items-center gap-0.5 sm:gap-1 gold-glow h-7 sm:h-11 px-1.5 sm:px-6 cursor-pointer transition-transform active:scale-95"
                >
                  <Plus className="w-3 h-3" />
                  <span className="font-pixel text-[5px] sm:text-[7px]">Anunciar</span>
                </button>

                {/* Botão MINHA CONTA / LOGIN */}
                {session ? (
                  <div className="relative">
                    {showInteresseBalloon && (
                      <div className="absolute top-full right-0 mt-2 z-50 w-[220px] sm:w-[260px] animate-in fade-in slide-in-from-top-2">
                        {/* Arrow */}
                        <div className="absolute -top-1.5 right-4 w-3 h-3 rotate-45 bg-card border-l border-t border-primary/60" />
                        <div className="tibia-border bg-card rounded-sm p-2 sm:p-3 shadow-lg gold-glow">
                          <button
                            type="button"
                            onClick={() => dismissInteresseBalloon(false)}
                            className="absolute top-1 right-1 text-muted-foreground hover:text-destructive cursor-pointer"
                            aria-label="Fechar"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <div className="flex items-start gap-2 pr-3">
                            <Bookmark className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                            <div className="font-pixel text-[7px] sm:text-[8px] text-foreground leading-relaxed">
                              {interessesMatches.length === 1
                                ? "Há 1 item de interesse com anúncio ativo!"
                                : `Há ${interessesMatches.length} itens de interesse com anúncios ativos!`}
                            </div>
                          </div>
                          <label className="mt-2 flex items-center gap-1.5 cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                            <input
                              type="checkbox"
                              className="w-3 h-3 cursor-pointer accent-primary"
                              onChange={(e) => { if (e.target.checked) dismissInteresseBalloon(true); }}
                            />
                            <span className="font-pixel text-[6px] sm:text-[7px]">Não exibir mais</span>
                          </label>
                        </div>
                      </div>
                    )}
                    {showDraftBalloon && (
                      <div
                        className="absolute right-0 z-50 w-[220px] sm:w-[260px] animate-in fade-in slide-in-from-top-2"
                        style={{ top: showInteresseBalloon ? "calc(100% + 110px)" : "calc(100% + 8px)" }}
                      >
                        <div className="absolute -top-1.5 right-4 w-3 h-3 rotate-45 bg-card border-l border-t border-primary/60" />
                        <div className="tibia-border bg-card rounded-sm p-2 sm:p-3 shadow-lg gold-glow">
                          <button
                            type="button"
                            onClick={() => dismissDraftBalloon(false)}
                            className="absolute top-1 right-1 text-muted-foreground hover:text-destructive cursor-pointer"
                            aria-label="Fechar"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <div className="flex items-start gap-2 pr-3">
                            <Package className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                            <div className="font-pixel text-[7px] sm:text-[8px] text-foreground leading-relaxed">
                              Você possui um anúncio em rascunho. Continue de onde parou!
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              dismissDraftBalloon(false);
                              setCreateOpen(true);
                            }}
                            className="mt-2 w-full tibia-btn rounded-sm font-pixel text-[7px] sm:text-[8px] py-1 cursor-pointer"
                          >
                            Continuar rascunho
                          </button>
                          <label className="mt-2 flex items-center gap-1.5 cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                            <input
                              type="checkbox"
                              className="w-3 h-3 cursor-pointer accent-primary"
                              onChange={(e) => { if (e.target.checked) dismissDraftBalloon(true); }}
                            />
                            <span className="font-pixel text-[6px] sm:text-[7px]">Não exibir mais</span>
                          </label>
                        </div>
                      </div>
                    )}
                    <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="relative flex items-center gap-1 px-2 tibia-border bg-card rounded-sm font-pixel text-[6px] sm:text-[8px] text-primary gold-glow-hover h-8 sm:h-11 transition-all duration-200 cursor-pointer">
                        {resolveAvatarUrl(session.avatar_url) ? (
                          <img
                            src={resolveAvatarUrl(session.avatar_url)!}
                            alt=""
                            className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-primary/30"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        )}
                        <span className="hidden md:inline">{session.apelido || session.nome || "Conta"}</span>

                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-3.5 h-3.5 flex items-center justify-center border border-background">
                            <span className="font-pixel text-[5px]">{unreadCount > 9 ? "!" : unreadCount}</span>
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="tibia-border bg-card min-w-[180px] font-pixel text-[8px]"
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      {/* Saldo de Coins */}
                      <button
                        onClick={() => navigate("/cliente?tab=cadastro")}
                        className="w-full flex items-center gap-2 px-2 py-2 text-muted-foreground hover:bg-secondary/30 hover:text-primary rounded-sm transition-colors cursor-pointer"
                        title="Ver saldo e resgatar coins"
                      >
                        <img src={tibiaCoinImg} alt="Coin" className="w-4 h-4" />
                        <span className="text-xs">Saldo:</span>
                        <span className="ml-auto bg-primary/20 text-primary px-1.5 py-0.5 rounded-full text-[9px] font-bold">
                          {coinBalance}
                        </span>
                      </button>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => {
                          setTimeout(() => navigate("/cliente"), 0);
                        }}
                        className="flex items-center gap-2 cursor-pointer text-xs"
                      >
                        <User className="w-3.5 h-3.5" />
                        Minha Conta
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          setTimeout(() => setMessagesDialogOpen(true), 0);
                        }}
                        className="flex items-center gap-2 cursor-pointer text-xs"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        Mensagens
                        {unreadCount > 0 && (
                          <span className="ml-auto bg-destructive text-destructive-foreground text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={(e) => {
                          setInteresseBalloonDismissed(true);
                          const hasInterestAds = (interessesSummary?.itemAnuncios?.length ?? 0) > 0;
                           if (hasInterestAds) {
                             // Limpa filtros para garantir que itens de interesse fiquem nos primeiros registros
                            setSelectedItems([]);
                            setFilterCompra(true);
                            setFilterVenda(true);
                            setFilterPropostas(false);
                            setServidorFilter([]);
                            setTierFilter([]);
                            setMinReais("");
                            setMaxReais("");
                            setMinCoins("");
                            setMaxCoins("");
                            setMinKk("");
                            setMaxKk("");
                            setAdvertiserFilter([]);
                            setShowMyList(false);
                            setShowExpired(false);
                            setShowDestaques(false);
                            setShowMyAds(false);
                            setCurrentPage(1);
                            // Aguarda a renderização para rolar até o topo da lista
                            setTimeout(() => {
                              const el = document.getElementById("anuncios-list-top");
                              if (el) {
                                const y = el.getBoundingClientRect().top + window.scrollY - 8;
                                window.scrollTo({ top: y, behavior: "smooth" });
                              }
                            }, 100);
                          } else {
                            setTimeout(() => navigate("/cliente?tab=interesses"), 0);
                          }
                        }}
                        className="flex items-center gap-2 cursor-pointer text-xs"
                      >
                        <Bookmark className="w-3.5 h-3.5" />
                        Itens de Interesse
                        <span
                          className={`ml-auto text-[8px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center ${
                            interessesMatches.length > 0
                              ? "bg-destructive text-destructive-foreground"
                              : "bg-primary/20 text-primary"
                          }`}
                        >
                          {interessesCount}
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => logout()}
                        className="flex items-center gap-2 cursor-pointer text-xs text-destructive focus:text-destructive"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Sair
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                ) : (
                  <button
                    onClick={() => setLoginOpen(true)}
                    className="flex items-center gap-1 px-3 tibia-border bg-card rounded-sm font-pixel text-[6px] sm:text-[8px] text-primary gold-glow-hover h-8 sm:h-11 transition-all duration-200 cursor-pointer"
                  >
                    <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Conta</span>
                  </button>
                )}

                {/* Messages Dialog */}
                {session && (
                  <Dialog open={messagesDialogOpen} onOpenChange={setMessagesDialogOpen}>
                    <DialogContent className="tibia-border bg-card max-w-md sm:max-w-lg font-pixel max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-pixel text-primary text-xs flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Mensagens
                          {unreadCount > 0 && (
                            <span className="bg-destructive text-destructive-foreground text-[8px] font-bold rounded-full px-1.5 py-0.5">
                              {unreadCount} não lida{unreadCount > 1 ? "s" : ""}
                            </span>
                          )}
                        </DialogTitle>
                        <DialogDescription className="text-[8px]">
                          Suas conversas na plataforma
                        </DialogDescription>
                      </DialogHeader>
                      <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>}>
                        <ClientMensagensTab onUnreadCountChange={setUnreadCount} />
                      </Suspense>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          </header>

          {/* Top navigation tabs: Itens / Personagens */}
          <div className="shrink-0 relative z-40 border-b border-border/50 bg-stone-dark/70 backdrop-blur-md">
            <div className="container max-w-5xl mx-auto px-2 sm:px-4 py-1.5 sm:py-2 grid grid-cols-3 gap-2 sm:gap-3">
              {([
                { id: "itens", label: "Itens", img: tabItensImg, desc: "Equipamentos & tesouros" },
                { id: "personagens", label: "Personagens", img: tabPersonagensImg, desc: "Heróis à venda" },
                { id: "coins", label: isMobile ? "Coins" : "Negociar coins", img: tabCoinsImg, desc: "Compra & venda" },
              ] as const).map((tab) => {
                const active = topTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setTopTab(tab.id)}
                    className={`group relative overflow-hidden rounded-md tibia-border transition-all duration-300 cursor-pointer ${
                      active
                        ? "ring-2 ring-primary gold-glow scale-[1.02]"
                        : "opacity-70 grayscale-[0.4] hover:opacity-100 hover:grayscale-0 hover:scale-[1.01]"
                    }`}
                  >
                    {/* Background image */}
                    <img
                      src={tab.img}
                      alt={tab.label}
                      loading="lazy"
                      width={512}
                      height={512}
                      className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${
                        active ? "scale-110" : "group-hover:scale-105"
                      }`}
                    />
                    {/* Dark gradient overlay for legibility (left dark for text, right reveals art) */}
                    <div
                      className={`absolute inset-0 transition-colors duration-300 ${
                        active
                          ? "bg-gradient-to-r from-stone-dark via-stone-dark/55 to-transparent"
                          : "bg-gradient-to-r from-stone-dark via-stone-dark/70 to-stone-dark/20"
                      }`}
                    />
                    {/* Shine sweep on active */}
                    {active && (
                      <div className="pointer-events-none absolute inset-0 overflow-hidden">
                        <div className="absolute -inset-y-2 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/25 to-transparent animate-[tab-shine_2.6s_ease-in-out_infinite]" />
                      </div>
                    )}
                    {/* Content */}
                    <div className="relative z-10 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 h-[40px] sm:h-[46px]">
                      <div className="flex flex-col items-start text-left min-w-0">
                        <span
                          className={`font-pixel text-[9px] sm:text-[11px] leading-none transition-colors ${
                            active ? "text-primary gold-text-glow" : "text-foreground group-hover:text-primary"
                          }`}
                        >
                          {tab.label}
                        </span>
                        <span className="hidden sm:block font-pixel text-[5.5px] sm:text-[6.5px] text-muted-foreground mt-0.5 truncate">
                          {tab.desc}
                        </span>
                      </div>
                    </div>
                    {/* Active bottom accent */}
                    <div
                      className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-300 ${
                        active ? "bg-primary shadow-[0_0_10px_hsl(var(--primary))]" : "bg-transparent"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>


          {/* Main Content */}
          <main id="main-scroll" className="flex-1 overflow-y-auto min-h-0 w-full">
            <div className={topTab === "personagens" ? "w-full px-3 sm:px-4 py-6" : topTab === "coins" ? "container max-w-lg mx-auto px-4 py-6" : "container max-w-5xl mx-auto px-4 py-6"}>
            {topTab === "coins" ? (
              <div className="animate-fade-in">
                {session ? (
                  <TibiaContainer title="Negociar Coins">
                    <Tabs value={coinsTab} onValueChange={(v) => setCoinsTab(v as "calculadora" | "historico")}>
                      <TabsList className="w-full bg-secondary/30 rounded-sm mb-4">
                        <TabsTrigger value="calculadora" className="flex-1 font-pixel text-[8px] data-[state=active]:bg-primary/20 data-[state=active]:text-primary rounded-sm">
                          <Coins className="w-3 h-3 mr-1" />
                          Calculadora
                        </TabsTrigger>
                        <TabsTrigger value="historico" className="flex-1 font-pixel text-[8px] data-[state=active]:bg-primary/20 data-[state=active]:text-primary rounded-sm">
                          <History className="w-3 h-3 mr-1" />
                          Histórico
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="calculadora">
                        <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>}>
                          <GoldCalculator session={session} onRequireLogin={requireLogin} />
                        </Suspense>
                      </TabsContent>
                      <TabsContent value="historico">
                        <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>}>
                          <TrocaCoinsHistoricoTab session={session} />
                        </Suspense>
                      </TabsContent>
                    </Tabs>
                  </TibiaContainer>
                ) : (
                  <TibiaContainer title="Calculadora de Troca">
                    <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>}>
                      <GoldCalculator session={session} onRequireLogin={requireLogin} />
                    </Suspense>
                  </TibiaContainer>
                )}
              </div>
            ) : topTab === "personagens" ? (
              <div className="animate-fade-in">
                {personagemLoading ? (
                  <div className="min-h-[40vh] flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                  </div>
                ) : personagemAnunciosMerged.length === 0 ? (
                  <div className="min-h-[40vh] flex flex-col items-center justify-center gap-2 text-center">
                    <User className="w-10 h-10 text-muted-foreground/40" />
                    <p className="font-pixel text-[9px] text-muted-foreground">
                      Nenhum personagem anunciado ainda
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col lg:flex-row gap-4 items-start">
                    {/* Sidebar filters (left on desktop) */}
                    <aside className="w-full lg:w-64 shrink-0 lg:sticky lg:top-2">
                      <PersonagemFilters
                        anuncios={personagemAnunciosMerged}
                        value={personagemFilters}
                        onChange={setPersonagemFilters}
                        vertical
                      />
                    </aside>
                    {/* Grid */}
                    <div className="flex-1 min-w-0 w-full">
                      {filteredPersonagemAnuncios.length === 0 ? (
                        <div className="min-h-[30vh] flex flex-col items-center justify-center gap-2 text-center">
                          <User className="w-10 h-10 text-muted-foreground/40" />
                          <p className="font-pixel text-[9px] text-muted-foreground">
                            Nenhum personagem encontrado com os filtros
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                          {filteredPersonagemAnuncios.map((anuncio) => (
                            <PersonagemCard
                              key={anuncio.id}
                              anuncioId={anuncio.id}
                              personagemNome={anuncio.personagem!.personagem_nome || anuncio.item_nome}
                              preco={anuncio.preco}
                              precoCoins={anuncio.preco_coins}
                              precoKk={anuncio.preco_kk}
                              moeda={anuncio.moeda}
                              servidor={anuncio.servidor}
                              destaque={anuncio.destaque}
                              apelido={anuncio.apelido}
                              isRevendedor={Boolean((anuncio as any).revendedor)}
                              expiraEm={anuncio.expira_em}
                              descricao={anuncio.descricao}
                              tier={(anuncio as any).tier}
                              temWhatsapp={!!(anuncio as any).tem_whatsapp}
                              temEmail={!!(anuncio as any).tem_email}
                              anuncianteId={(anuncio as any).anunciante_id || undefined}
                              anuncianteAvatarUrl={(anuncio as any).anunciante_avatar_url || undefined}
                              preferenciasContato={(anuncio as any).preferencias_contato || ["plataforma"]}
                              sessionContato={session?.contato}
                              sessionToken={session?.token}
                              hasActiveIntermediacao={
                                !!intermediacaoByAnuncio[anuncio.id] || publicIntermediatingAnuncios.has(anuncio.id)
                              }
                              onIntermediacaoClick={
                                intermediacaoByAnuncio[anuncio.id]
                                  ? () => setActiveIntermediacaoId(intermediacaoByAnuncio[anuncio.id])
                                  : undefined
                              }
                              onRequireLogin={(action) => requireLogin(action)}
                              onOpenProfile={(id) => setProfileDialogId(id)}
                              onClick={() => handlePersonagemView(anuncio)}
                              isOwner={checkIsOwner(session, anuncio as any, myAnuncioIds)}
                              anuncioStatus={anuncio.status}
                              onAnuncioAction={handleAnuncioActionRefresh}
                              personagem={anuncio.personagem!}
                              createdAt={(anuncio as any).aprovado_em || anuncio.created_at}
                              destaqueInicio={(anuncio as any).destaque_inicio || null}
                              renovacaoPendente={!!(anuncio as any).renovacao_pendente}
                              taxaPaga={!!(anuncio as any).taxa_paga}
                              metodoPagamentoTaxa={(anuncio as any).metodo_pagamento_taxa || null}
                              valorTaxa={(anuncio as any).valor_taxa ?? null}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
            <>

            {/* Action bar - Adicionado relative e z-40 para sobrepor o carrossel */}
            <div
              className="space-y-3 mb-6 w-full relative z-40 animate-fade-in"
            >
              {/* Top row: search + collapse toggle */}
              <div className="flex flex-wrap gap-2 items-end">
                {/* Container da busca ajustado para não estourar */}
                <div className="flex-1 min-w-0 max-w-full">
                  <ItemSearchCombobox
                    anuncios={comboboxAnuncios}
                    itemDetails={itemDetails}
                    selected={selectedItems}
                    onChange={setSelectedItems}
                    onOpenAnuncio={(id) => navigate(`/item/${id}`)}
                  />
                </div>
                {/* Toggle filters button */}
                <button
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className={`flex items-center gap-1 px-2 h-8 sm:h-9 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer self-end ${filtersOpen ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                >
                  <SlidersHorizontal className="w-3 h-3" />
                  Filtros
                  <ChevronUp
                    className={`w-3 h-3 transition-transform duration-200 ${filtersOpen ? "" : "rotate-180"}`}
                  />
                </button>
                {/* View mode toggle - always visible, desktop only */}
                {!isMobile && (
                  <div className="flex items-center gap-0.5 self-end tibia-border rounded-sm overflow-hidden h-8 sm:h-9">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`h-full px-2 flex items-center transition-all cursor-pointer ${viewMode === "grid" ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`h-full px-2 flex items-center transition-all cursor-pointer ${viewMode === "list" ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                    >
                      <List className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {/* Meus Anúncios toggle */}
                {session && (
                  <button
                    onClick={() => setShowMyAds(!showMyAds)}
                    className={`flex items-center gap-1 px-2 h-8 sm:h-9 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer self-end ${showMyAds ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                  >
                    {showMyAds && myAnuncioIdsLoading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Crown className="w-3 h-3" />
                    )}
                    Meus Anúncios
                  </button>
                )}
                {/* Minha Lista toggle - desktop only, on mobile it's inside the filter dialog */}
                {!isMobile && (
                  <button
                    onClick={() => {
                      if (!showMyList) {
                        requireLogin(() => setShowMyList(true));
                      } else {
                        setShowMyList(false);
                      }
                    }}
                    className={`flex items-center gap-1 px-2 h-8 sm:h-9 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer self-end ${showMyList ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                  >
                    <Bookmark className="w-3 h-3" />
                    Minha Lista
                  </button>
                )}
              </div>

              {/* Mobile fullscreen filter dialog */}
              {isMobile && filtersOpen && (
                <div className="fixed inset-0 z-[200] bg-background flex flex-col overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
                    <span className="font-pixel text-[10px] text-primary gold-text-glow italic">Filtros</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setFiltersOpen(false)}
                        className="font-pixel text-[9px] text-muted-foreground cursor-pointer px-3 py-1 tibia-border rounded-sm bg-secondary/50"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => setFiltersOpen(false)}
                        className="font-pixel text-[9px] text-primary cursor-pointer px-3 py-1 tibia-border rounded-sm bg-primary/10"
                      >
                        Aplicar
                      </button>
                    </div>
                  </div>

                  {/* Filter content - no visible scrollbar */}
                  <div
                    className="flex-1 overflow-auto px-4 py-3 space-y-4"
                    style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
                  >
                    {/* Minha Lista - full width, highlighted */}
                    <button
                      onClick={() => {
                        if (!showMyList) {
                          requireLogin(() => setShowMyList(true));
                        } else {
                          setShowMyList(false);
                        }
                      }}
                      className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 tibia-border rounded-sm font-pixel text-[9px] transition-all duration-200 cursor-pointer ${showMyList ? "bg-primary/30 text-primary border-primary/50" : "bg-primary/10 text-primary"}`}
                    >
                      <Bookmark className="w-4 h-4" />
                      Minha Lista
                    </button>

                    {/* Servidor - full width */}
                    <div className="[&>div]:w-full [&>div>button]:max-w-none [&>div>button]:min-w-0 [&>div>button]:w-full">
                      <ServerCombobox
                        selected={servidorFilter}
                        onChange={setServidorFilter}
                      />
                    </div>

                    {/* Tier - full width */}
                    <div className="[&>div]:w-full [&>div>button]:max-w-none [&>div>button]:min-w-0 [&>div>button]:w-full">
                      <TierCombobox selected={tierFilter} onChange={setTierFilter} />
                    </div>

                    {/* Anunciante - full width */}
                    <div className="[&>div]:w-full [&>div>button]:max-w-none [&>div>button]:min-w-0 [&>div>button]:w-full">
                      <AdvertiserCombobox
                        anuncios={comboboxAnuncios}
                        selected={advertiserFilter}
                        onChange={setAdvertiserFilter}
                        onOpenProfile={(id) => setProfileDialogId(id)}
                      />
                    </div>

                    {/* Tipo toggles - full width */}
                    <div className="flex flex-col gap-1">
                      <label className="font-pixel text-[7px] text-muted-foreground flex items-center gap-1">
                        <ShoppingCart className="w-3 h-3" /> Tipo
                      </label>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => setFilterCompra(!filterCompra)}
                          className={`flex-1 px-2 py-2 tibia-border rounded-sm font-pixel text-[8px] transition-all duration-200 cursor-pointer ${filterCompra ? "bg-blue-600/30 text-blue-400" : "bg-card text-muted-foreground"}`}
                        >
                          Compra
                        </button>
                        <button
                          onClick={() => setFilterVenda(!filterVenda)}
                          className={`flex-1 px-2 py-2 tibia-border rounded-sm font-pixel text-[8px] transition-all duration-200 cursor-pointer ${filterVenda ? "bg-red-600/30 text-red-400" : "bg-card text-muted-foreground"}`}
                        >
                          Venda
                        </button>
                        <button
                          onClick={() => setFilterPropostas(!filterPropostas)}
                          className={`flex-1 px-2 py-2 tibia-border rounded-sm font-pixel text-[8px] transition-all duration-200 cursor-pointer ${filterPropostas ? "bg-blue-400/30 text-blue-300" : "bg-card text-muted-foreground"}`}
                        >
                          Propostas
                        </button>
                      </div>
                    </div>

                    {/* Destaques + Expirados - full width, same line */}
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setShowDestaques(!showDestaques)}
                        className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 tibia-border rounded-sm font-pixel text-[8px] transition-all duration-200 cursor-pointer ${showDestaques ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                      >
                        <Star className={`w-3.5 h-3.5 ${showDestaques ? "fill-primary" : ""}`} />
                        Destaques
                      </button>
                      <button
                        onClick={() => setShowExpired(!showExpired)}
                        className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 tibia-border rounded-sm font-pixel text-[8px] transition-all duration-200 cursor-pointer ${showExpired ? "bg-muted text-muted-foreground" : "bg-card text-muted-foreground"}`}
                      >
                        <Clock className="w-3.5 h-3.5" />
                        Finalizados
                      </button>
                    </div>

                    {/* Price filters - full width */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => setShowPriceFilters(!showPriceFilters)}
                        className={`flex items-center gap-1 px-3 py-2 tibia-border rounded-sm font-pixel text-[8px] transition-all duration-200 cursor-pointer w-full ${showPriceFilters ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                      >
                        <DollarSign className="w-3.5 h-3.5" />
                        Valores
                        <ChevronUp
                          className={`w-3 h-3 transition-transform duration-200 ml-auto ${showPriceFilters ? "" : "rotate-180"}`}
                        />
                      </button>
                      {showPriceFilters && (
                        <div className="space-y-2 pt-1">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex flex-col gap-0.5">
                              <label className="font-pixel text-[7px] text-muted-foreground">R$ Min</label>
                              <input
                                type="number"
                                placeholder="0"
                                min="0"
                                value={minReais}
                                onChange={(e) => setMinReais(e.target.value.replace(/^-/, ""))}
                                className="tibia-input rounded-sm text-xs py-2 w-full"
                              />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <label className="font-pixel text-[7px] text-muted-foreground">R$ Max</label>
                              <input
                                type="number"
                                placeholder="∞"
                                min="0"
                                value={maxReais}
                                onChange={(e) => setMaxReais(e.target.value.replace(/^-/, ""))}
                                className="tibia-input rounded-sm text-xs py-2 w-full"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex flex-col gap-0.5">
                              <label className="font-pixel text-[7px] text-muted-foreground">TC Min</label>
                              <input
                                type="number"
                                placeholder="0"
                                min="0"
                                value={minCoins}
                                onChange={(e) => setMinCoins(e.target.value.replace(/^-/, ""))}
                                className="tibia-input rounded-sm text-xs py-2 w-full"
                              />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <label className="font-pixel text-[7px] text-muted-foreground">TC Max</label>
                              <input
                                type="number"
                                placeholder="∞"
                                min="0"
                                value={maxCoins}
                                onChange={(e) => setMaxCoins(e.target.value.replace(/^-/, ""))}
                                className="tibia-input rounded-sm text-xs py-2 w-full"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex flex-col gap-0.5">
                              <label className="font-pixel text-[7px] text-amber-400">KK Min</label>
                              <input
                                type="number"
                                placeholder="0"
                                min="0"
                                value={minKk}
                                onChange={(e) => setMinKk(e.target.value.replace(/^-/, ""))}
                                className="tibia-input rounded-sm text-xs py-2 w-full"
                              />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <label className="font-pixel text-[7px] text-amber-400">KK Max</label>
                              <input
                                type="number"
                                placeholder="∞"
                                min="0"
                                value={maxKk}
                                onChange={(e) => setMaxKk(e.target.value.replace(/^-/, ""))}
                                className="tibia-input rounded-sm text-xs py-2 w-full"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sticky footer Apply button — always visible */}
                  <div
                    className="shrink-0 border-t border-border/40 bg-background px-4 py-3"
                    style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
                  >
                    <button
                      onClick={() => setFiltersOpen(false)}
                      className="w-full tibia-btn rounded-sm font-pixel text-[10px] text-primary-foreground gold-glow py-3 cursor-pointer active:scale-95 transition-transform"
                    >
                      Aplicar Filtros
                    </button>
                  </div>
                </div>
              )}

              {/* Desktop collapsible filters */}
              {!isMobile && (
                <AnimatePresence>
                  {filtersOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: "hidden" }}
                      onAnimationComplete={(def: any) => {
                        if (def?.opacity === 1) {
                          const el = document.getElementById("filters-collapsible");
                          if (el) el.style.overflow = "visible";
                        }
                      }}
                      id="filters-collapsible"
                    >
                      <div className="flex flex-wrap gap-2 items-end pt-2">
                        <ServerCombobox
                          selected={servidorFilter}
                          onChange={setServidorFilter}
                        />
                        <TierCombobox selected={tierFilter} onChange={setTierFilter} />
                        <AdvertiserCombobox
                          anuncios={comboboxAnuncios}
                          selected={advertiserFilter}
                          onChange={setAdvertiserFilter}
                          onOpenProfile={(id) => setProfileDialogId(id)}
                        />
                        {/* Tipo toggles */}
                        <div className="flex flex-col gap-0.5">
                          <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
                            <ShoppingCart className="w-2.5 h-2.5" /> Tipo
                          </label>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setFilterCompra(!filterCompra)}
                              className={`h-[26px] px-2 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer ${filterCompra ? "bg-blue-600/30 text-blue-400" : "bg-card text-muted-foreground"}`}
                            >
                              Compra
                            </button>
                            <button
                              onClick={() => setFilterVenda(!filterVenda)}
                              className={`h-[26px] px-2 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer ${filterVenda ? "bg-red-600/30 text-red-400" : "bg-card text-muted-foreground"}`}
                            >
                              Venda
                            </button>
                            <button
                              onClick={() => setFilterPropostas(!filterPropostas)}
                              className={`h-[26px] px-2 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer ${filterPropostas ? "bg-blue-400/30 text-blue-300" : "bg-card text-muted-foreground"}`}
                            >
                              Propostas
                            </button>
                          </div>
                        </div>
                        {/* Destaques toggle */}
                        <button
                          onClick={() => setShowDestaques(!showDestaques)}
                          className={`h-[26px] flex items-center gap-1 px-2 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer self-end ${showDestaques ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                        >
                          <Star className={`w-3 h-3 ${showDestaques ? "fill-primary" : ""}`} />
                          Destaques
                        </button>
                        {/* Finalizados toggle */}
                        <button
                          onClick={() => setShowExpired(!showExpired)}
                          className={`h-[26px] flex items-center gap-1 px-2 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer self-end ${showExpired ? "bg-muted text-muted-foreground" : "bg-card text-muted-foreground"}`}
                        >
                          <Clock className="w-3 h-3" />
                          Finalizados
                        </button>
                        {/* Valores toggle */}
                        <button
                          onClick={() => setShowPriceFilters(!showPriceFilters)}
                          className={`h-[26px] flex items-center gap-1 px-2 tibia-border rounded-sm font-pixel text-[7px] transition-all duration-200 cursor-pointer self-end ${showPriceFilters ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}
                        >
                          <DollarSign className="w-3 h-3" />
                          Valores
                          <ChevronUp
                            className={`w-2.5 h-2.5 transition-transform duration-200 ${showPriceFilters ? "" : "rotate-180"}`}
                          />
                        </button>
                      </div>

                      {/* Price filters - shown only when Valores is active */}
                      <AnimatePresence>
                        {showPriceFilters && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="overflow-hidden"
                          >
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-2">
                              <div className="flex flex-col gap-0.5">
                                <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
                                  <DollarSign className="w-2.5 h-2.5" /> R$ Min
                                </label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  min="0"
                                  value={minReais}
                                  onChange={(e) => setMinReais(e.target.value.replace(/^-/, ""))}
                                  className="tibia-input rounded-sm text-xs w-full py-1.5"
                                />
                              </div>
                              <div className="flex flex-col gap-0.5">
                                <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
                                  <DollarSign className="w-2.5 h-2.5" /> R$ Max
                                </label>
                                <input
                                  type="number"
                                  placeholder="∞"
                                  min="0"
                                  value={maxReais}
                                  onChange={(e) => setMaxReais(e.target.value.replace(/^-/, ""))}
                                  className="tibia-input rounded-sm text-xs w-full py-1.5"
                                />
                              </div>
                              <div className="flex flex-col gap-0.5">
                                <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
                                  <Coins className="w-2.5 h-2.5" /> TC Min
                                </label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  min="0"
                                  value={minCoins}
                                  onChange={(e) => setMinCoins(e.target.value.replace(/^-/, ""))}
                                  className="tibia-input rounded-sm text-xs w-full py-1.5"
                                />
                              </div>
                              <div className="flex flex-col gap-0.5">
                                <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
                                  <Coins className="w-2.5 h-2.5" /> TC Max
                                </label>
                                <input
                                  type="number"
                                  placeholder="∞"
                                  min="0"
                                  value={maxCoins}
                                  onChange={(e) => setMaxCoins(e.target.value.replace(/^-/, ""))}
                                  className="tibia-input rounded-sm text-xs w-full py-1.5"
                                />
                              </div>
                              <div className="flex flex-col gap-0.5">
                                <label className="font-pixel text-[6px] text-amber-400 flex items-center gap-1">
                                  KK Min
                                </label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  min="0"
                                  value={minKk}
                                  onChange={(e) => setMinKk(e.target.value.replace(/^-/, ""))}
                                  className="tibia-input rounded-sm text-xs w-full py-1.5"
                                />
                              </div>
                              <div className="flex flex-col gap-0.5">
                                <label className="font-pixel text-[6px] text-amber-400 flex items-center gap-1">
                                  KK Max
                                </label>
                                <input
                                  type="number"
                                  placeholder="∞"
                                  min="0"
                                  value={maxKk}
                                  onChange={(e) => setMaxKk(e.target.value.replace(/^-/, ""))}
                                  className="tibia-input rounded-sm text-xs w-full py-1.5"
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>

            {/* Featured Carousel */}
            {!isLoading && !showExpired && !showDestaques && config?.carrossel_destaque === "true" && (
              <div className="relative z-10">
                <FeaturedCarousel
                  anuncios={filteredForCarousel}
                  onCardClick={handleCardClick}
                  onRequireLogin={(action) => requireLogin(action)}
                  onApelidoClick={handleApelidoClick}
                  rotationTime={carouselRotationTime}
                  rotateAll={carouselRotateAll}
                  fitToScreen={carouselFitToScreen}
                  sessionContato={session?.contato}
                  sessionToken={session?.token}
                  sessionPerfilId={session?.perfil_id}
                  ownedAnuncioIds={myAnuncioIds}
                  onAnuncioAction={handleAnuncioActionRefresh}
                  intermediatingAnuncios={publicIntermediatingAnuncios}
                  interesseAnuncioIds={interesseAnuncioIds}
                  loteFullItems={loteFullItemsMap}
                />
              </div>
            )}

            {/* Anúncios counter - lista geral */}
            {!isLoading && displayTotal > 0 && (
              <div id="anuncios-list-top" className="flex items-center justify-between mb-2">
                <span className="font-pixel text-[9px] sm:text-[11px] text-primary gold-text-glow tracking-wider">
                  <span className="text-primary">{displayTotal}</span> {showExpired ? "finalizados" : "anúncios"}
                  {displayTotal > PAGE_SIZE && (
                    <span className="text-muted-foreground/60 ml-1">
                      (pág. {currentPage} de {totalPages})
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Grid */}
            {isLoading || (isFetching && filteredAnuncios.length === 0) ? (
              viewMode === "list" && !isMobile ? (
                <div className="flex flex-col gap-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <MarketplaceListItemSkeleton key={i} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <MarketplaceCardSkeleton key={i} />
                  ))}
                </div>
              )
            ) : filteredAnuncios.length === 0 && filteredForCarousel.filter((a) => a.destaque || ((a as any).destaque_fim && new Date((a as any).destaque_fim).getTime() > Date.now())).length === 0 ? (
              <div className="text-center py-16 animate-fade-in">
                <Swords className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-pixel text-[10px] text-muted-foreground mb-2">
                  {showExpired ? "Nenhum anúncio finalizado encontrado" : "Nenhum anúncio encontrado"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {showExpired
                    ? "Não há itens finalizados com os filtros selecionados."
                    : "Seja o primeiro a anunciar um item!"}
                </p>
              </div>
            ) : viewMode === "list" && !isMobile ? (
              <div className="flex flex-col gap-2">
                {displayedGroupedAnuncios.map((group, idx) => {
                  if (group.type === "single") {
                    const anuncio = group.anuncio;
                    const details = anuncio.item_id && itemDetails ? itemDetails[anuncio.item_id] : null;
                    const isOwnerVal = checkIsOwner(session, anuncio as any, myAnuncioIds);
                    return (
                      <div
                        key={anuncio.id}
                        className={`${
                          isExpired(anuncio) && anuncio.status !== "pausado" && !isOwnerVal
                            ? "opacity-60 grayscale pointer-events-none"
                            : ""
                        }`}
                      >
                        <MarketplaceListItem
                          anuncioId={anuncio.id}
                          itemNome={anuncio.item_nome}
                          tipo={anuncio.tipo as "compra" | "venda"}
                          preco={anuncio.preco}
                          moeda={anuncio.moeda}
                          precoCoins={anuncio.preco_coins}
                          precoKk={(anuncio as any).preco_kk}
                          servidor={anuncio.servidor}
                          destaque={anuncio.destaque}
                          descricao={anuncio.descricao}
                          createdAt={(anuncio as any).aprovado_em || anuncio.created_at}
                          expiraEm={anuncio.expira_em}
                          destaqueInicio={(anuncio as any).destaque_inicio || null}
                          destaqueFim={(anuncio as any).destaque_fim || null}
                          temWhatsapp={!!(anuncio as any).tem_whatsapp}
                          temEmail={!!(anuncio as any).tem_email}
                          level={details?.levelrequired}
                          attack={details?.attack}
                          defense={details?.defense}
                          upgradeclass={(anuncio as any).tier}
                          imageUrl={details?.image_url}
                          apelido={(anuncio as any).apelido || undefined}
                          isCustom={!!(anuncio as any).item_custom}
                          isOwner={isOwnerVal}
                          anuncioStatus={anuncio.status}
                          sessionContato={session?.contato}
                          sessionToken={session?.token}
                          anuncianteId={(anuncio as any).anunciante_id || undefined}
                          anuncianteAvatarUrl={(anuncio as any).anunciante_avatar_url || undefined}
                          preferenciasContato={(anuncio as any).preferencias_contato || ["plataforma"]}
                          onClick={() => handleCardClick(anuncio)}
                          onRequireLogin={(action) => requireLogin(action)}
                          onApelidoClick={handleApelidoClick}
                          onAnuncioAction={handleAnuncioActionRefresh}
                          renovacaoPendente={!!(anuncio as any).renovacao_pendente}
                          taxaPaga={!!(anuncio as any).taxa_paga}
                          metodoPagamentoTaxa={(anuncio as any).metodo_pagamento_taxa || null}
                          hasActiveIntermediacao={
                            !!intermediacaoByAnuncio[anuncio.id] || publicIntermediatingAnuncios.has(anuncio.id)
                          }
                          abacatepayUrl={(anuncio as any).abacatepay_url || null}
                          valorTaxa={(anuncio as any).valor_taxa ?? null}
                          isInteresseMatch={isInteresseAd(anuncio)}
                          isRevendedor={Boolean(anuncio.revendedor)}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={group.loteId}>
                      <BatchStackedCards count={group.anuncios.length} mode="list" expanded={expandedLotes.has(group.loteId)} onToggleExpand={() => toggleLote(group.loteId)}>
                        {group.anuncios.map((anuncio, batchIdx) => {
                          const details = anuncio.item_id && itemDetails ? itemDetails[anuncio.item_id] : null;
                          const isOwnerVal = checkIsOwner(session, anuncio as any, myAnuncioIds);
                          return (
                            <div key={anuncio.id}>
                              <MarketplaceListItem
                                anuncioId={anuncio.id}
                                itemNome={anuncio.item_nome}
                                tipo={anuncio.tipo as "compra" | "venda"}
                                preco={anuncio.preco}
                                moeda={anuncio.moeda}
                                precoCoins={anuncio.preco_coins}
                                precoKk={(anuncio as any).preco_kk}
                                servidor={anuncio.servidor}
                                destaque={anuncio.destaque}
                                descricao={anuncio.descricao}
                                createdAt={(anuncio as any).aprovado_em || anuncio.created_at}
                                expiraEm={anuncio.expira_em}
                                destaqueInicio={(anuncio as any).destaque_inicio || null}
                                destaqueFim={(anuncio as any).destaque_fim || null}
                                temWhatsapp={!!(anuncio as any).tem_whatsapp}
                                temEmail={!!(anuncio as any).tem_email}
                                level={details?.levelrequired}
                                attack={details?.attack}
                                defense={details?.defense}
                                upgradeclass={(anuncio as any).tier}
                                imageUrl={details?.image_url}
                                apelido={(anuncio as any).apelido || undefined}
                                isCustom={!!(anuncio as any).item_custom}
                                isOwner={isOwnerVal}
                                anuncioStatus={anuncio.status}
                                sessionContato={session?.contato}
                                sessionToken={session?.token}
                                anuncianteId={(anuncio as any).anunciante_id || undefined}
                                anuncianteAvatarUrl={(anuncio as any).anunciante_avatar_url || undefined}
                                preferenciasContato={(anuncio as any).preferencias_contato || ["plataforma"]}
                                onClick={() => handleCardClick(anuncio)}
                                onRequireLogin={(action) => requireLogin(action)}
                                onApelidoClick={handleApelidoClick}
                                onAnuncioAction={handleAnuncioActionRefresh}
                                renovacaoPendente={!!(anuncio as any).renovacao_pendente}
                                taxaPaga={!!(anuncio as any).taxa_paga}
                                metodoPagamentoTaxa={(anuncio as any).metodo_pagamento_taxa || null}
                                hasActiveIntermediacao={
                                  !!intermediacaoByAnuncio[anuncio.id] || publicIntermediatingAnuncios.has(anuncio.id)
                                }
                                abacatepayUrl={batchIdx === 0 ? ((anuncio as any).abacatepay_url || null) : null}
                                valorTaxa={batchIdx === 0 ? group.anuncios.reduce((sum, a) => sum + Number((a as any).valor_taxa || 0), 0) : ((anuncio as any).valor_taxa ?? null)}
                                isInteresseMatch={isInteresseAd(anuncio)}
                                isRevendedor={Boolean(anuncio.revendedor)}
                              />
                            </div>
                          );
                        })}
                      </BatchStackedCards>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {displayedGroupedAnuncios.map((group, idx) => {
                  if (group.type === "single") {
                    const anuncio = group.anuncio;
                    const details = anuncio.item_id && itemDetails ? itemDetails[anuncio.item_id] : null;
                    const isOwnerVal = checkIsOwner(session, anuncio as any, myAnuncioIds);
                    return (
                      <div
                        key={anuncio.id}
                        className={`${isExpired(anuncio) && anuncio.status !== "pausado" && !isOwnerVal ? "opacity-60 grayscale pointer-events-none" : ""} ${isOwnerVal ? "bg-primary/10 border-t border-primary/20" : ""}`}
                      >
                        <MarketplaceCard
                          anuncioId={anuncio.id}
                          itemNome={anuncio.item_nome}
                          tipo={anuncio.tipo as "compra" | "venda"}
                          preco={anuncio.preco}
                          moeda={anuncio.moeda}
                          precoCoins={anuncio.preco_coins}
                          precoKk={(anuncio as any).preco_kk}
                          servidor={anuncio.servidor}
                          destaque={anuncio.destaque}
                          descricao={anuncio.descricao}
                          createdAt={(anuncio as any).aprovado_em || anuncio.created_at}
                          expiraEm={anuncio.expira_em}
                          destaqueInicio={(anuncio as any).destaque_inicio || null}
                          destaqueFim={(anuncio as any).destaque_fim || null}
                          updatedAt={(anuncio as any).updated_at || null}
                          temWhatsapp={!!(anuncio as any).tem_whatsapp}
                          temEmail={!!(anuncio as any).tem_email}
                          level={details?.levelrequired}
                          attack={details?.attack}
                          defense={details?.defense}
                          upgradeclass={(anuncio as any).tier}
                          imageUrl={details?.image_url}
                          apelido={(anuncio as any).apelido || undefined}
                          isCustom={!!(anuncio as any).item_custom}
                          isOwner={isOwnerVal}
                          anuncioStatus={anuncio.status}
                          sessionContato={session?.contato}
                          sessionToken={session?.token}
                          anuncianteId={(anuncio as any).anunciante_id || undefined}
                          anuncianteAvatarUrl={(anuncio as any).anunciante_avatar_url || undefined}
                          preferenciasContato={(anuncio as any).preferencias_contato || ["plataforma"]}
                          onClick={() => handleCardClick(anuncio)}
                          onRequireLogin={(action) => requireLogin(action)}
                          onApelidoClick={handleApelidoClick}
                          onAnuncioAction={handleAnuncioActionRefresh}
                          renovacaoPendente={!!(anuncio as any).renovacao_pendente}
                          taxaPaga={!!(anuncio as any).taxa_paga}
                          metodoPagamentoTaxa={(anuncio as any).metodo_pagamento_taxa || null}
                          hasActiveIntermediacao={
                            !!intermediacaoByAnuncio[anuncio.id] || publicIntermediatingAnuncios.has(anuncio.id)
                          }
                          onIntermediacaoClick={
                            intermediacaoByAnuncio[anuncio.id]
                              ? () => setActiveIntermediacaoId(intermediacaoByAnuncio[anuncio.id])
                              : undefined
                          }
                          abacatepayUrl={(anuncio as any).abacatepay_url || null}
                          valorTaxa={(anuncio as any).valor_taxa ?? null}
                          isInteresseMatch={isInteresseAd(anuncio)}
                          isRevendedor={Boolean(anuncio.revendedor)}
                        />
                      </div>
                    );
                  }
                  return (
                    <BatchStackedCards key={group.loteId} count={group.anuncios.length} mode="grid" expanded={expandedLotes.has(group.loteId)} onToggleExpand={() => toggleLote(group.loteId)}>
                      {group.anuncios.map((anuncio, batchIdx) => {
                        const details = anuncio.item_id && itemDetails ? itemDetails[anuncio.item_id] : null;
                        const isOwnerVal = checkIsOwner(session, anuncio as any, myAnuncioIds);
                        return (
                          <div
                            key={anuncio.id}
                            className={isOwnerVal ? "bg-primary/10 border-t border-primary/20" : ""}
                          >
                            <MarketplaceCard
                              anuncioId={anuncio.id}
                              itemNome={anuncio.item_nome}
                              tipo={anuncio.tipo as "compra" | "venda"}
                              preco={anuncio.preco}
                              moeda={anuncio.moeda}
                              precoCoins={anuncio.preco_coins}
                              precoKk={(anuncio as any).preco_kk}
                              servidor={anuncio.servidor}
                              destaque={anuncio.destaque}
                              descricao={anuncio.descricao}
                              createdAt={(anuncio as any).aprovado_em || anuncio.created_at}
                              expiraEm={anuncio.expira_em}
                              destaqueInicio={(anuncio as any).destaque_inicio || null}
                              destaqueFim={(anuncio as any).destaque_fim || null}
                              updatedAt={(anuncio as any).updated_at || null}
                              temWhatsapp={!!(anuncio as any).tem_whatsapp}
                              temEmail={!!(anuncio as any).tem_email}
                              level={details?.levelrequired}
                              attack={details?.attack}
                              defense={details?.defense}
                              upgradeclass={(anuncio as any).tier}
                              imageUrl={details?.image_url}
                              apelido={(anuncio as any).apelido || undefined}
                              isCustom={!!(anuncio as any).item_custom}
                              isOwner={isOwnerVal}
                              anuncioStatus={anuncio.status}
                              sessionContato={session?.contato}
                              sessionToken={session?.token}
                              anuncianteId={(anuncio as any).anunciante_id || undefined}
                              anuncianteAvatarUrl={(anuncio as any).anunciante_avatar_url || undefined}
                              preferenciasContato={(anuncio as any).preferencias_contato || ["plataforma"]}
                              onClick={() => handleCardClick(anuncio)}
                              onRequireLogin={(action) => requireLogin(action)}
                              onApelidoClick={handleApelidoClick}
                              onAnuncioAction={handleAnuncioActionRefresh}
                              renovacaoPendente={!!(anuncio as any).renovacao_pendente}
                              taxaPaga={!!(anuncio as any).taxa_paga}
                              metodoPagamentoTaxa={(anuncio as any).metodo_pagamento_taxa || null}
                              hasActiveIntermediacao={
                                !!intermediacaoByAnuncio[anuncio.id] || publicIntermediatingAnuncios.has(anuncio.id)
                              }
                              onIntermediacaoClick={
                                intermediacaoByAnuncio[anuncio.id]
                                  ? () => setActiveIntermediacaoId(intermediacaoByAnuncio[anuncio.id])
                                  : undefined
                              }
                              abacatepayUrl={batchIdx === 0 ? ((anuncio as any).abacatepay_url || null) : null}
                              valorTaxa={batchIdx === 0 ? group.anuncios.reduce((sum, a) => sum + Number((a as any).valor_taxa || 0), 0) : ((anuncio as any).valor_taxa ?? null)}
                              isBatchStacked={batchIdx === 0}
                              batchCount={group.anuncios.length}
                              batchItemNames={batchIdx === 0 ? (loteFullItemsMap.get(group.loteId)?.map((it) => it.item_nome) || group.anuncios.map((a) => a.item_nome)) : undefined}
                              batchItemMeta={batchIdx === 0 ? (loteFullItemsMap.get(group.loteId) || group.anuncios.map((a) => ({ item_nome: a.item_nome, destaque: !!(a as any).destaque }))) : undefined}
                              isInteresseMatch={isInteresseAd(anuncio)}
                              isRevendedor={Boolean(anuncio.revendedor)}
                            />
                          </div>
                        );
                      })}
                    </BatchStackedCards>
                  );
                })}
              </div>
            )}

            {/* Pagination controls */}
            {totalPages > 1 && !isLoading && (
              <div className="flex items-center justify-center gap-2 mt-6 mb-2">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); document.getElementById("main-scroll")?.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="tibia-btn rounded-sm px-3 py-1.5 font-pixel text-[8px] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronLeft className="w-3 h-3" /> Anterior
                </button>
                <div className="flex items-center gap-1">
                  {(() => {
                    const pages: (number | "...")[] = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      if (currentPage > 3) pages.push("...");
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
                      if (currentPage < totalPages - 2) pages.push("...");
                      pages.push(totalPages);
                    }
                    return pages.map((p, i) =>
                      p === "..." ? (
                        <span key={`e${i}`} className="font-pixel text-[8px] text-muted-foreground px-1">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => { setCurrentPage(p as number); document.getElementById("main-scroll")?.scrollTo({ top: 0, behavior: "smooth" }); }}
                          className={`w-7 h-7 rounded-sm font-pixel text-[8px] cursor-pointer transition-all ${
                            p === currentPage
                              ? "bg-primary text-primary-foreground"
                              : "tibia-border bg-card text-muted-foreground hover:text-primary"
                          }`}
                        >
                          {p}
                        </button>
                      )
                    );
                  })()}
                </div>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); document.getElementById("main-scroll")?.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="tibia-btn rounded-sm px-3 py-1.5 font-pixel text-[8px] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  Próxima <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
            </>
            )}
            </div>

            {/* Footer inline em mobile (não fica grudado na parte inferior) */}
            <div className="sm:hidden mt-6">
              <AppFooter />
            </div>
          </main>

          {/* Footer fixo apenas em desktop */}
          <div className="shrink-0 hidden sm:block">
            <AppFooter />
          </div>


          <Suspense fallback={null}>
            <CreateAnuncioDialog
              open={createOpen}
              onOpenChange={(o) => {
                setCreateOpen(o);
                // When the dialog closes, refresh ads so any newly-created
                // pending-payment ad becomes visible without manual refresh.
                if (!o) handleAnuncioActionRefresh();
              }}
            />
            <CreateAnuncioPersonagemDialog
              open={createPersonagemOpen}
              onOpenChange={setCreatePersonagemOpen}
              onCreated={() => {
                refetchPersonagens();
                handleAnuncioActionRefresh();
              }}
            />
            <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} onLoginSuccess={handleLoginSuccess} />
          </Suspense>

          <Dialog open={chooseTypeOpen} onOpenChange={setChooseTypeOpen}>
            <DialogContent className="max-w-xs">
              <DialogHeader>
                <DialogTitle className="font-pixel text-[10px] text-primary">O que deseja anunciar?</DialogTitle>
                <DialogDescription className="font-pixel text-[7px]">
                  Selecione o tipo de anúncio que deseja criar.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  onClick={() => { setChooseTypeOpen(false); setCreateOpen(true); }}
                  className="tibia-btn rounded-sm flex flex-col items-center gap-2 py-4 cursor-pointer transition-transform active:scale-95"
                >
                  <Package className="w-6 h-6" />
                  <span className="font-pixel text-[7px]">Item</span>
                </button>
                <button
                  onClick={() => { setChooseTypeOpen(false); setCreatePersonagemOpen(true); }}
                  className="tibia-btn rounded-sm flex flex-col items-center gap-2 py-4 cursor-pointer transition-transform active:scale-95"
                >
                  <User className="w-6 h-6" />
                  <span className="font-pixel text-[7px]">Personagem</span>
                </button>
              </div>
            </DialogContent>
          </Dialog>

          {detailItem && (
            <ItemDetailModal
              open={!!detailItem}
              onOpenChange={(open) => !open && setDetailItem(null)}
              item={detailItem}
              onRequireLogin={(action) => requireLogin(action)}
              sessionContato={session?.contato}
              sessionToken={session?.token}
              sessionPerfilId={session?.perfil_id}
              ownedAnuncioIds={myAnuncioIds}
              onAnuncioAction={handleAnuncioActionRefresh}
            />
          )}
          <Suspense fallback={null}>
            {session && (
              <BugReportDialog
                open={bugReportOpen}
                onOpenChange={setBugReportOpen}
                reportadoPor={session.apelido || session.contato || "Anônimo"}
              />
            )}
            <AdvertiserProfileDialog
              open={!!profileDialogId}
              onOpenChange={(open) => {
                if (!open) setProfileDialogId(null);
              }}
              perfilId={profileDialogId || ""}
              onFilterByAdvertiser={(apelido) => {
                setAdvertiserFilter([apelido]);
              }}
            />
          </Suspense>
        </div>
      </div>

      {/* Floating WhatsApp button - outside scroll container */}
      {whatsappNumero && (
        <a
          href={`https://wa.me/${whatsappNumero}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-600 hover:bg-green-700 rounded-full flex items-center justify-center shadow-lg shadow-green-900/40 transition-all hover:scale-110 active:scale-90"
        >
          <MessageCircle className="w-7 h-7 text-white" />
        </a>
      )}

      <Suspense fallback={null}>
        {activeIntermediacaoId && session && !intermediacaoMinimized && (
          <IntermedioChatDialog
            open={!intermediacaoMinimized}
            onOpenChange={(open) => {
              if (!open) {
                setIntermediacaoMinimized(true);
              } else {
                setUnreadIntermediacaoIds((prev) => {
                  const next = new Set(prev);
                  next.delete(activeIntermediacaoId!);
                  return next;
                });
              }
            }}
            intermediacaoId={activeIntermediacaoId}
            sessionContato={session.contato}
            sessionToken={session.token}
            onStatusChange={() => {}}
            tabs={activeIntermediacoes.map((inter) => {
              const isInteressado = inter.interessado_id === session?.perfil_id;
              const otherParty = isInteressado
                ? inter.anunciante?.apelido || inter.anunciante?.nome || "Anunciante"
                : inter.interessado?.apelido || inter.interessado?.nome || "Interessado";
              return {
                id: inter.id,
                itemNome: inter.anuncios?.item_nome || "Item",
                itemImageUrl: inter._item_image_url || null,
                otherParty,
              };
            })}
            onSwitchTab={(id) => {
              setActiveIntermediacaoId(id);
              setUnreadIntermediacaoIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              });
            }}
            unreadIds={unreadIntermediacaoIds}
          />
        )}
      </Suspense>

      {/* Floating minimized intermediation bar */}
      {intermediacaoMinimized && hasActiveIntermediacao && session && (
          <div
            className="fixed bottom-6 left-6 z-50 flex items-center gap-1 bg-primary text-primary-foreground rounded-full shadow-lg shadow-primary/30 animate-fade-in"
          >
            {activeIntermediacoes.length > 1 ? (
              activeIntermediacoes.map((inter) => {
                const itemNome = inter.anuncios?.item_nome || "Item";
                const isInteressado = inter.interessado_id === session?.perfil_id;
                const otherParty = isInteressado
                  ? inter.anunciante?.apelido || inter.anunciante?.nome || "Anunciante"
                  : inter.interessado?.apelido || inter.interessado?.nome || "Interessado";
                return (
                  <button
                    key={inter.id}
                    onClick={() => {
                      setActiveIntermediacaoId(inter.id);
                      setIntermediacaoMinimized(false);
                      setUnreadIntermediacaoIds((prev) => {
                        const next = new Set(prev);
                        next.delete(inter.id);
                        return next;
                      });
                    }}
                    className="relative flex items-center gap-1.5 px-3 py-2.5 hover:bg-primary-foreground/10 rounded-full transition-colors"
                    title={`${itemNome} — ${otherParty}`}
                  >
                    {unreadIntermediacaoIds.has(inter.id) && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-destructive rounded-full border border-primary" />
                    )}
                    {inter._item_image_url ? (
                      <img src={inter._item_image_url} alt="" className="w-5 h-5 object-contain rounded" />
                    ) : (
                      <Handshake className="w-4 h-4" />
                    )}
                    <span className="font-pixel text-[8px] max-w-[60px] truncate">{itemNome}</span>
                  </button>
                );
              })
            ) : (
              <button
                onClick={() => {
                  setIntermediacaoMinimized(false);
                  if (activeIntermediacaoId) {
                    setUnreadIntermediacaoIds((prev) => {
                      const next = new Set(prev);
                      next.delete(activeIntermediacaoId);
                      return next;
                    });
                  }
                }}
                className="relative flex items-center gap-2 px-4 py-3 hover:bg-primary/90 rounded-full transition-colors"
              >
                {unreadIntermediacaoIds.size > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-destructive rounded-full border border-primary" />
                )}
                <Handshake className="w-5 h-5" />
                <span className="font-pixel text-[9px]">Intermédio em andamento</span>
              </button>
            )}
          </div>
      )}

      {/* Blocked user dialog */}
      <Dialog open={showBloqueado} onOpenChange={setShowBloqueado}>
        <DialogContent className="tibia-border bg-card max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-pixel text-xs text-destructive text-center flex items-center justify-center gap-2">
              <Ban className="w-5 h-5" /> CONTA BLOQUEADA
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground text-center">
              Sua conta foi bloqueada pelo administrador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">Entre em contato com o administrador para mais informações.</p>
            {whatsappNumero && (
              <a
                href={`https://wa.me/${whatsappNumero}?text=${encodeURIComponent("Olá, minha conta foi bloqueada e gostaria de entender o motivo.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="tibia-btn rounded-sm flex items-center justify-center gap-2 w-full"
                style={{ backgroundColor: "#25D366" }}
              >
                <MessageCircle className="w-4 h-4" />
                <span className="font-pixel text-[8px] text-white">Falar no WhatsApp</span>
              </a>
            )}
            <button onClick={() => setShowBloqueado(false)} className="tibia-tab rounded-sm px-4 py-2 w-full">
              <span className="font-pixel text-[8px]">Fechar</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Index;
